// src/components/dashboard/Dashboard.jsx
import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import LogoutButton from "../session/logout_container";
import "./dashboard.css";
import { io } from "socket.io-client";

import * as EnrollmentAPI from "../../util/enrollment_api_util"; // ✅ authenticated client
import * as ApprenticeshipAPI from "../../util/apprenticeship_api_util";


// ✅ Child components
import DashboardHeader from "./DashboardHeader";
import CourseListPanel from "./CourseListPanel";
import InstructorPanel from "./InstructorPanel";
import PhotoPackageReadyPanel from "./PhotoPackageReadyPanel";
import ApprenticeshipPanel from "./ApprenticeshipPanel";
import GMArtistsPanel from "./GMArtistsPanel";
import SelectedPersonPanel from "./SelectedPersonPanel";
import ProfileOverlay from "./ProfileOverlay";
import LogoutOverlay from "./LogoutOverlay";
import ProShopCard from "../shop/ProShopCard";
import PostCourseViewer from "../courses/PostCourseViewer"; // adjust path

import CourseResourcesAdminContainer from "../course_resources/CourseResourcesAdminContainer";
import StudentResourcesPanelContainer from "../course_resources/StudentResourcesPanelContainer";




import "./dashboardskeleton.css"

// example shape (seed later)
export const POST_COURSE_MODULES_BY_KEY = {
  nanoblading_hairstrokes: {
    title: "Nano Brows — Post-Course",
    sections: [
      /**
       * SECTION 1 — PENCIL SHARPENING (FOUNDATION)
       */
      {
        id: "nano_pc_1",
        index: 1,
        title: "Pencil Sharpening — Duck Beak Technique",
        blocks: [
          {
            type: "text",
            text:
              "Before nano work ever touches skin, your pencil work must be clean and controlled. We sharpen using a straight razor to create a thin, flat ‘duck beak’ edge—this gives you crisp outlines and hairlike planning strokes without bulky lines."
          },
          {
            type: "text",
            text:
              "Technique: keep the blade angle shallow and your pressure light. You are shaving the pencil—never chopping. Graze each side to form a flat face with a thin edge."
          },
          {
            type: "text",
            text:
              "Quality standard: the edge should create clean, fine strokes with minimal pressure. If the line looks wide, fuzzy, or inconsistent, reshape the edge and slow down."
          },
          {
            type: "video",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/PencilSharpening.mp4",
            alt: "Pencil sharpening — duck beak technique",
            muted: true
          }
        ],
        autoPass: false,
        assignment: {
          title: "Assignment: Pencil Sharpening Practice",
          instructions:
            "Upload at least 1 photo showing: (1) your sharpened duck-beak edge and (2) a row of clean, hairlike practice strokes. Clear lighting and close focus are required.",
          minFiles: 1,
          maxFiles: 3
        },
        quiz: {
          title: "Pencil Sharpening Check",
          questions: [
            {
              q: "What is the main purpose of the duck-beak shape?",
              options: [
                "To soften the pencil lead",
                "To create crisp, controlled outlines and hairlike planning strokes",
                "To reduce mapping time only",
                "To make shading easier"
              ],
              answerIndex: 1
            },
            {
              q: "How should the razor contact the pencil for best control?",
              options: [
                "Steep angle with firm pressure",
                "Shallow angle with light, grazing pressure",
                "Straight down chopping motion",
                "Rapid scraping with heavy pressure"
              ],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 2 — BROW MAPPING REVIEW
       */
      {
        id: "nano_pc_2",
        index: 2,
        title: "Brow Mapping Review",
        blocks: [
          {
            type: "text",
            text:
              "Strong nano work starts with clean, balanced mapping. Mapping controls symmetry, arch placement, and stroke flow. If mapping is off, even great technique will heal wrong."
          },
          {
            type: "text",
            text:
              "Map with the face relaxed and the head level. Recheck symmetry from a step back—front view and slight angles—before you commit to patterns."
          },
          {
            type: "video",
            url: "https://cosacademyvideos.s3.us-east-2.amazonaws.com/ModelHeadMapping.mp4",
            alt: "Brow mapping demonstration",
            muted: true
          },
          {
            type: "resource",
            url: "https://cosmetinkacademyappuploads.s3.us-east-2.amazonaws.com/course_resources/67d8adb88d60558fe072ffa0/1769564878172.pdf",
            label: "Brow Mapping Worksheet (PDF)"
          },
          {
            type: "text",
            text:
              "Quality standard: crisp lines, symmetry of all points, clean arch placement, and a believable tail direction (follow the bone!). Avoid thick, fuzzy outlines—those hide mistakes instead of fixing them."
          }
        ],
        assignment: {
          title: "Assignment: Brow Mapping Practice",
          instructions:
            "Submit clear photos of 3 completed brow mappings (worksheet, flat latex, or latex face). Include at least one straight-on photo for symmetry review.",
          minFiles: 1,
          maxFiles: 6
        },
        quiz: {
          title: "Mapping Check",
          questions: [
            {
              q: "Why is accurate brow mapping critical before nano strokes?",
              options: [
                "It helps the strokes heal darker",
                "It ensures symmetry, balance, and correct stroke flow",
                "It reduces trauma to the skin automatically",
                "It speeds up the appointment"
              ],
              answerIndex: 1
            },
            {
              q: "Before committing to strokes, what should you verify?",
              options: [
                "Only the client’s preferred color",
                "Symmetry, head position, and anchor points",
                "Needle depth settings only",
                "Final shading plan"
              ],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 3 — HAIRSTROKE PATTERN DESIGN
       */
      {
        id: "nano_pc_3",
        index: 3,
        title: "Hairstroke Pattern Design — Flow & Spacing",
        blocks: [
          {
            type: "text",
            text:
              "Nano patterns must look intentional and realistic. Build structure first—then let the strokes feel natural. Divide the brow into three zones: front, middle, and tail."
          },
          {
            type: "text",
            text:
              "Create a connection line from the top of the front zone toward the middle/tail divider. Each zone receives three strokes (9 total per brow as your training standard)."
          },
          {
            type: "text",
            text:
              "Bottom strokes begin on the lower outline and flow into the connection line. Top strokes begin at the top outline and flow downward. Avoid parallel lines—real hair changes direction subtly."
          },
          {
            type: "text",
            text:
              "Quality standard: consistent spacing, clean starts/ends, believable flow, and no crowding. If strokes touch or stack, it will heal heavier and less hairlike."
          },
          {
            type: "resource",
            url: "https://cosmetinkacademyappuploads.s3.us-east-2.amazonaws.com/course_resources/67d8adb88d60558fe072ffa0/1769524261051.pdf",
            label: "Hairstroke Pattern Worksheet"
          },
          {
            type: "resource",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/PreDrawStrokes.pdf",
            label: "Pre-Drawn Guideline Strokes (PDF)"
          }
        ],
        assignment: {
          title: "Assignment: Hairstroke Patterns",
          instructions:
            "Submit 3 full pairs of hairstroke patterns. Use the worksheet or pre-outlined latex. Prioritize spacing and flow over “filling space.”",
          minFiles: 1,
          maxFiles: 6
        },
        quiz: {
          title: "Pattern Knowledge Check",
          questions: [
            {
              q: "How many hairstrokes are placed in each brow zone in this training standard?",
              options: ["One", "Two", "Three", "Five"],
              answerIndex: 2
            },
            {
              q: "Where should the bottom strokes begin for the most natural flow?",
              options: [
                "Above the outline",
                "On the lower outline flowing into the connection line",
                "In the center of the brow",
                "At the tail only"
              ],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 4 — NANO TECHNIQUE THEORY
       */
      {
        id: "nano_pc_4",
        index: 4,
        title: "Nano Technique — Depth, Stretch, and Control",
        blocks: [
          {
            type: "text",
            text:
              "Nano success is built on three controllables: (1) consistent stretch, (2) consistent depth, and (3) clean entry/exit. Inconsistency in any of these leads to uneven healing and patchy retention."
          },
          {
            type: "resource",
            url: "https://cosmetinkacademyappuploads.s3.us-east-2.amazonaws.com/course_resources/67d8adb88d60558fe072ffa0/1769564385070.pdf",
            label: "Nano Technique Worksheet"
          },
          {
            type: "text",
            text:
              "Quality standard: strokes should be fine, crisp, and evenly saturated—not thick, scratchy, or blown out. Your needle should start out of the skin, enter with intention, and exit immediately."
          }
        ],
        quiz: {
          title: "Nano Technique Check",
          questions: [
            {
              q: "Where should the needle start during a nano stroke?",
              options: [
                "Already in the skin",
                "Hovering above the skin",
                "Dragging along the surface",
                "Pressed firmly into the skin"
              ],
              answerIndex: 1
            },
            {
              q: "What causes the most inconsistent healing in nano strokes?",
              options: [
                "Even pressure and spacing",
                "Inconsistent depth or pressure (often paired with unstable stretch)",
                "Light, controlled passes",
                "Starting above the skin"
              ],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 5 — HAND POSITION & MOVEMENT
       */
      {
        id: "nano_pc_5",
        index: 5,
        title: "Hand Position & Movement — Clean Entry/Exit",
        blocks: [
          {
            type: "text",
            text:
              "Hand position controls stroke quality. Keep your wrist lifted (don’t collapse), pinky out for stability, and use your non-dominant hand to stretch the skin consistently."
          },
          {
            type: "text",
            text:
              "Nano uses a small, controlled pendulum motion. The needle starts out of the skin, enters swiftly, and exits immediately—no dragging."
          },
          {
            type: "text",
            text:
              "Quality standard: stable stretch, consistent angle, and repeatable motion. If your wrist drops or your stretch changes, your strokes will change too."
          },
          {
            type: "video",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/properhandposition.mp4",
            alt: "Proper nano hand position",
            muted: false
          }
        ],
        assignment: {
          title: "Assignment: Hand Position Practice",
          instructions:
            "Upload a short clip showing your hand position and stretch on latex, while creating a nano hairstroke. We need to clearly see a lifted wrist and consistent stretch. If available, use a phone stand or have someone film your hand for a stable, close view.",
          minFiles: 1,
          maxFiles: 3
        },
        quiz: {
          title: "Hand Position Check",
          questions: [
            {
              q: "Why must the wrist stay lifted during nano strokes?",
              options: [
                "For comfort only",
                "To allow consistent pendulum motion and prevent dragging",
                "To reduce pigment saturation",
                "To speed up the process"
              ],
              answerIndex: 1
            },
            {
              q: "What is the role of your non-dominant hand during nano strokes?",
              options: [
                "Hold pigment only",
                "Stretch and stabilize the skin",
                "Mark the outline only",
                "Control needle depth directly"
              ],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 6 — PASS ONE (GUIDELINE STROKES)
       */
      {
        id: "nano_pc_6",
        index: 6,
        title: "Pass One — Guideline Strokes",
        blocks: [
          {
            type: "text",
            text:
              "Pass one establishes your guideline strokes. This pass is about consistency: clean entry/exit, consistent spacing, and controlled saturation."
          },
          {
            type: "text",
            text:
              "In our video you’ll see me working from the front strokes to the tail, but on your client as a beginner it’s often more beneficial to start at the tail and work your way up to the front of the brow — just like in class."
          },
          {
            type: "text",
            text:
              "Then you can check your work and make sure your pressure, color choice, and finger movement are working well in the skin. Check each hairstroke as you go to ensure it’s there."
          },
          {
            type: "video",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/NanoSteps/PassOne_GuidelineStrokes.mp4",
            alt: "Pass One — guideline strokes",
            muted: false
          }
        ],
        assignment: {
          title: "Assignment: Pass One — Guideline Strokes",
          instructions:
            "Submit one pair (left and right brow) of your best pass-one guideline strokes on latex. Clean with baby oil or coconut oil so we can clearly evaluate spacing and entry/exit.",
          minFiles: 1,
          maxFiles: 6
        },
        quiz: {
          title: "Pass One Check",
          questions: [
            {
              q: "What is the main goal of pass one?",
              options: [
                "Maximum density",
                "Clean guideline strokes with consistent spacing",
                "Darkest possible strokes",
                "Final shading"
              ],
              answerIndex: 1
            },
            {
              q: "Where should you start your pass-one strokes as a beginner?",
              options: [
                "Starting at the tail and working forward is often best for beginners",
                "Only at the front strokes",
                "Only in the middle of the brow",
                "Only at the tail and never move forward"
              ],
              answerIndex: 0
            },
            {
              q: "Why should you check each hairstroke as you go?",
              options: [
                "To make the strokes darker",
                "To ensure your pressure, color choice, and finger movement are working well in the skin and each stroke is present",
                "To speed up the procedure",
                "To avoid mapping"
              ],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 7 — PASS TWO (SMALLER STROKES)
       */
      {
        id: "nano_pc_7",
        index: 7,
        title: "Pass Two — Smaller Strokes & Density Control",
        blocks: [
          {
            type: "text",
            text:
              "Pass two builds density with smaller strokes. Keep pressure light and controlled—this is where many artists start overcrowding."
          },
          {
            type: "text",
            text:
              "Quality standard: density increases without losing hairlike separation. If it starts to look like solid shading, you’re stacking too tightly or working too heavy."
          },
          {
            type: "video",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/NanoSteps/PassTwo_SmallerStrokes.mp4",
            alt: "Pass Two — smaller strokes",
            muted: false
          }
        ],
        assignment: {
          title: "Assignment: Pass Two — Density Build",
          instructions:
            "Submit one pair (left and right brow) after pass two. We’re grading spacing, stroke separation, and controlled density.",
          minFiles: 1,
          maxFiles: 6
        },
        quiz: {
          title: "Pass Two Check",
          questions: [
            {
              q: "What is the focus during pass two?",
              options: [
                "Final darkness",
                "Building density with smaller, controlled strokes",
                "Only outlining",
                "Shading the tail"
              ],
              answerIndex: 1
            },
            {
              q: "What happens if you overcrowd strokes during pass two?",
              options: [
                "Softer healing",
                "Heavier, less realistic results",
                "Cleaner outlines",
                "Better symmetry"
              ],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 8 — PASS THREE (MINI & SELECTIVE DARKER STROKES)
       */
      {
        id: "nano_pc_8",
        index: 8,
        title: "Pass Three — Selective Refinement",
        blocks: [
          {
            type: "text",
            text:
              "Pass three is selective refinement. Add mini strokes only where needed to balance the pattern—do not blanket-pack the entire brow."
          },
          {
            type: "text",
            text:
              "Watch ‘skin response’ (even on latex, mimic the discipline). Consistent, controlled passes create better healed outcomes than heavy pressure."
          },
          {
            type: "video",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/NanoSteps/PassThree_MiniStrokes%26DarkerStrokes.mp4",
            alt: "Pass Three — mini & selective darker strokes",
            muted: false
          }
        ],
        assignment: {
          title: "Assignment: Pass Three — Refinement",
          instructions:
            "Submit one pair (left and right brow) after pass three. The goal is refinement without crowding or overworking.",
          minFiles: 1,
          maxFiles: 6
        },
        quiz: {
          title: "Pass Three Check",
          questions: [
            {
              q: "What should guide your decision to add density in pass three?",
              options: [
                "Only the timer",
                "Balance, even saturation, and what truly needs support",
                "How dark you want it immediately",
                "Client pain level only"
              ],
              answerIndex: 1
            },
            {
              q: "What is a sign you are overworking?",
              options: [
                "Consistent spacing",
                "A harsh, packed look or visible trauma/over-saturation",
                "Soft gradient",
                "Clean edges"
              ],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 9 — TOUCH-UPS
       */
      {
        id: "nano_pc_9",
        index: 9,
        title: "Nano Brow Touch-Ups — Strategy & Mindset",
        blocks: [
          {
            type: "text",
            text:
              "A touch-up is not a brand-new set—it’s targeted refinement. Preserve what healed well and support what faded or lacks balance."
          },
          {
            type: "text",
            text:
              "Start with assessment: where did pigment fade, where is density uneven, and where does the flow need support? Work lightly and only where needed."
          },
          {
            type: "text",
            text:
              "Patchiness usually comes from inconsistent depth/pressure, unstable stretch, moving too fast, or overworking one area while underworking another."
          },
          {
            type: "text",
            text:
              "Corrective approach: slow down, stabilize your stretch, keep pressure consistent, and add clean, intentional strokes only in the faded areas."
          },
          {
            type: "text",
            text:
              "Client factors matter: poor pre-care/aftercare, oily skin, sun exposure, and picking can reduce retention. Reinforce proper expectations and aftercare."
          },
          {
            type: "text",
            text:
              "Expectation: a first session should heal softer—often a shade or two lighter—so the touch-up can layer a finished, natural density without heaviness."
          }
        ],
        quiz: {
          title: "Touch-Up Check",
          questions: [
            {
              q: "What is the main goal of a touch-up?",
              options: [
                "Create a brand-new brow",
                "Refine the healed result with light, targeted work",
                "Add as much density as possible everywhere",
                "Redo the entire pattern"
              ],
              answerIndex: 1
            },
            {
              q: "What should guide where you place touch-up strokes?",
              options: [
                "Only the original outline",
                "Areas of fade, imbalance, and needed support",
                "Where the brow looks darkest",
                "Anywhere you see blank space"
              ],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 10 — FULL NANO (FIRST-SESSION STANDARD)
       */
      {
        id: "nano_pc_10",
        index: 10,
        title: "Full Nano Brow — First Session Expectations",
        blocks: [
          {
            type: "text",
            text:
              "After the first session, a full nano brow should look clean, balanced, patterned, and client-ready—soft, wearable, and realistic."
          },
          {
            type: "text",
            text:
              "Quality standard: intentional flow, consistent spacing, and natural density. If it looks crowded or solid, it won’t heal like fine hair."
          },
          {
            type: "video",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/NanoSteps/FinishedNanoOnSkin.mp4",
            alt: "Finished nano brows on skin",
            muted: true
          }
        ],
        assignment: {
          title: "Final Assignment: Full Nano Brow Sets",
          instructions:
            "Submit 3 full pairs of completed nano brows on latex. We’re grading pattern quality, spacing, softness, and a finished look a paying client would choose.",
          minFiles: 1,
          maxFiles: 6
        },
        quiz: {
          title: "Final Readiness Check",
          questions: [
            {
              q: "What should a full nano brow look like after the first session?",
              options: [
                "Overly dark and heavy",
                "Clean, balanced, and client-ready",
                "Sparse with no defined pattern",
                "Only outlined with no density"
              ],
              answerIndex: 1
            },
            {
              q: "Which detail most elevates the final result?",
              options: [
                "Crowding strokes for darkness",
                "Soft, consistent density with intentional flow",
                "Skipping refinements",
                "Max pressure for boldness"
              ],
              answerIndex: 1
            }
          ]
        }
      }
    ]
  },

  ombre_powder_brows: {
    title: "Ombré Powder Brows — Post-Course",
    sections: [
      /**
       * SECTION 1 — PENCIL SHARPENING (FOUNDATION)
       */
      {
        id: "ombre_pc_1",
        index: 1,
        title: "Pencil Sharpening — Duck Beak Technique",
        blocks: [
          {
            type: "text",
            text:
              "Before ombré work ever touches skin, your pencil lines must be crisp. We sharpen into a thin ‘duck beak’ edge so your outline stays clean and your map stays balanced."
          },
          {
            type: "text",
            text:
              "Technique: shallow blade angle, light pressure, and controlled shaving strokes. Never chop—shape a flat face with a thin edge."
          },
          {
            type: "text",
            text:
              "Quality standard: your outline should be crisp without looking thick. Thick mapping hides asymmetry and leads to uneven ombré edges."
          },
          {
            type: "video",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/PencilSharpening.mp4",
            alt: "Pencil sharpening — duck beak technique",
            muted: true
          }
        ],
        autoPass: false,
        assignment: {
          title: "Assignment: Pencil Sharpening Practice",
          instructions:
            "Upload at least 1 photo showing: (1) your sharpened duck-beak edge and (2) a practice line to show how crisp and sharp the pencil draws. Clear lighting and close focus are required.",
          minFiles: 1,
          maxFiles: 3
        },
        quiz: {
          title: "Pencil Sharpening Check",
          questions: [
            {
              q: "What is the main purpose of the duck-beak shape?",
              options: [
                "To soften the pencil lead",
                "To create crisp, controlled outlines and clean planning lines",
                "To reduce mapping time only",
                "To make shading easier"
              ],
              answerIndex: 1
            },
            {
              q: "How should the razor contact the pencil for best control?",
              options: [
                "Steep angle with firm pressure",
                "Shallow angle with light, grazing pressure",
                "Straight down chopping motion",
                "Rapid scraping with heavy pressure"
              ],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 2 — BROW MAPPING REVIEW
       */
      {
        id: "ombre_pc_2",
        index: 2,
        title: "Brow Mapping Review",
        blocks: [
          {
            type: "text",
            text:
              "Strong ombré results start with clean mapping. Your map determines symmetry, outline crispness, and where your gradient will sit."
          },
                  {
            type: "text",
            text:
              "Watch me map a mannequin face in the video below.  There is no sound, so just follow along with your brow mapping step-by-step:"
          },
          {
            type: "video",
            url: "https://cosacademyvideos.s3.us-east-2.amazonaws.com/ModelHeadMapping.mp4",
            alt: "Brow mapping demonstration",
            muted: true
          },
          {
            type: "resource",
            url: "https://cosmetinkacademyappuploads.s3.us-east-2.amazonaws.com/course_resources/67d8adb88d60558fe072ffa0/1769564878172.pdf",
            label: "Brow Mapping Worksheet (PDF)"
          },
          {
            type: "text",
            text:
              "Acceptable practice surfaces: paper, flat latex, or a latex face. Quality standard: crisp lines, clean corners, balanced height, and a believable tail direction."
          }
        ],
        assignment: {
          title: "Assignment: Brow Mapping Practice",
          instructions:
            "Submit at least 1 photo of your brow mapping using your mapping sticker (paper, flat latex, or latex face). We’re grading crisp lines, clean shape, and symmetry.",
          minFiles: 1,
          maxFiles: 6
        },
        quiz: {
          title: "Mapping Check",
          questions: [
            {
              q: "What is the main goal of brow mapping before ombré work?",
              options: [
                "To make the front darker",
                "To create symmetry, clean shape, and proper balance",
                "To speed up the appointment",
                "To reduce pigment usage"
              ],
              answerIndex: 1
            },
            {
              q: "Which mapping surface is acceptable for this assignment?",
              options: [
                "Only a live model",
                "Only a latex face",
                "Paper, latex face, or flat latex",
                "Only the worksheet PDF"
              ],
              answerIndex: 2
            }
          ]
        }
      },

      /**
       * SECTION 3 — OMBRÉ TECHNIQUE THEORY
       */
      {
        id: "ombre_pc_3",
        index: 3,
        title: "Ombré Technique — Theory Review",
        blocks: [
          {
            type: "text",
            text:
              "Ombré is controlled saturation in layers. Your priorities: pixel consistency, edge softness, pressure control, and a clean gradient (soft front → denser tail)."
          },
          {
            type: "resource",
            url: "https://cosmetinkacademyappuploads.s3.us-east-2.amazonaws.com/course_resources/67d8adb88d60558fe072ffa0/1769564702549.pdf",
            label: "Ombré Technique Worksheet"
          },
          {
            type: "text",
            text:
              "Quality standard: no harsh blocks, no striping, and no overworked edges. Layer softly and re-evaluate between passes."
          }
        ],
        quiz: {
          title: "Ombré Technique Check",
          questions: [
            {
              q: "What creates a clean, soft ombré transition?",
              options: [
                "Heavy outline first",
                "Even pressure, consistent rhythm, and controlled layering",
                "Deep needle depth",
                "Fast circular passes"
              ],
              answerIndex: 1
            },
            {
              q: "Why is layering important in ombré work?",
              options: [
                "It makes the front darker immediately",
                "It builds soft saturation without harsh edges",
                "It replaces the outline step",
                "It removes the need for mapping"
              ],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 4 — HAND POSITION & MOVEMENT
       */
      {
        id: "ombre_pc_4",
        index: 4,
        title: "Hand Position & Movement — Pendulum Control",
        blocks: [
          {
            type: "text",
            text:
              "Hand position controls pixel quality. Keep your wrist lifted, pinky out for stability, and a consistent stretch with your non-dominant hand."
          },
          {
            type: "text",
            text:
              "Your needle should start out of the skin, enter softly with intention, and exit cleanly. A stable rhythm is what keeps pixels even."
          },
          {
            type: "video",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/properhandposition.mp4",
            alt: "Proper hand position",
            muted: false
          },
          {
            type: "text",
            text:
              "Ombré pendulum movement is smooth and controlled. Small, consistent motion builds soft saturation without overworking."
          },
          {
            type: "video",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/ombrebrowclassmaterials/ombre_pendulum.mp4",
            alt: "Ombré pendulum movement",
            muted: false
          }
        ],
        assignment: {
          title: "Assignment: Hand Position",
          instructions:
            "Upload at least 1 photo or short clip showing your hand position during ombré passes (pinky out, wrist lifted, consistent stretch, controlled pendulum).",
          minFiles: 1,
          maxFiles: 6
        },
        quiz: {
          title: "Hand Position Check",
          questions: [
            {
              q: "Why must the wrist stay lifted during ombré passes?",
              options: [
                "For comfort only",
                "To allow pendulum motion and keep pixels consistent",
                "To increase needle depth",
                "To speed up the process"
              ],
              answerIndex: 1
            },
            {
              q: "Where should the needle start before each pass motion?",
              options: [
                "Already in the skin",
                "Hovering above the skin",
                "Dragging along the surface",
                "Pressed firmly into the skin"
              ],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 5 — PASS ONE
       */
      {
        id: "ombre_pc_5",
        index: 5,
        title: "Tattooing the Ombré Brow — Pass One (Foundation)",
        blocks: [
          {
            type: "text",
            text:
              "Pass one builds your foundation. Keep pressure light and your rhythm consistent. This pass should look soft, even, and controlled—not dark."
          },
          {
            type: "video",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/ombrebrowclassmaterials/ombre_pass1.mp4",
            alt: "Ombré pass one",
            muted: false
          }
        ],
        assignment: {
          title: "Assignment: Pass One",
          instructions:
            "Submit at least 1 photo of a pair of brows after pass one. We’re grading spacing, softness, and even foundation saturation.",
          minFiles: 1,
          maxFiles: 6
        },
        quiz: {
          title: "Pass One Check",
          questions: [
            {
              q: "What is the primary goal of pass one?",
              options: [
                "Maximum darkness",
                "Even, soft foundation with consistent rhythm",
                "Only outlining",
                "Final gradient"
              ],
              answerIndex: 1
            },
            {
              q: "What does good pass one control prevent later?",
              options: [
                "Soft pixels",
                "Overpacking, harsh blocks, and uneven saturation",
                "Symmetry",
                "Mapping"
              ],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 6 — PASS TWO
       */
      {
        id: "ombre_pc_6",
        index: 6,
        title: "Tattooing the Ombré Brow — Pass Two (Build)",
        blocks: [
          {
            type: "text",
            text:
              "Pass two builds density while keeping the front soft. Layer carefully and keep your motion consistent."
          },
          {
            type: "video",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/ombrebrowclassmaterials/ombre_pass2.mp4",
            alt: "Ombré pass two",
            muted: false
          }
        ],
        assignment: {
          title: "Assignment: Pass Two",
          instructions:
            "Submit at least 1 photo of a pair of brows after pass two. We want clean layering, controlled density, and a soft front.",
          minFiles: 1,
          maxFiles: 6
        },
        quiz: {
          title: "Pass Two Check",
          questions: [
            {
              q: "What should stay soft during pass two?",
              options: ["Only the tail", "The front gradient", "The outline", "The arch only"],
              answerIndex: 1
            },
            {
              q: "What happens if you overwork during pass two?",
              options: [
                "Softer heal",
                "Blotchy/uneven saturation and increased trauma risk",
                "Cleaner gradient",
                "Faster completion"
              ],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 7 — PASS THREE / PERFECTING
       */
      {
        id: "ombre_pc_7",
        index: 7,
        title: "Tattooing the Ombré Brow — Pass Three (Perfecting)",
        blocks: [
          {
            type: "text",
            text:
              "Pass three builds saturation from the tail to the middle of the brow. This adds further density and creates the finished ombré effect."
          },
          {
            type: "video",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/ombrebrowclassmaterials/ombre_pass3.mp4",
            alt: "Ombré pass three",
            muted: false
          },
          {
            type: "text",
            text:
              "Perfecting pass: add density only where needed and refine edges without packing or sharpening the front. Keep your motion controlled so the front stays soft and the gradient remains clean."
          },
          {
            type: "video",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/ombrebrowclassmaterials/ombre_perfecting_pass.mp4",
            alt: "Ombré perfecting pass",
            muted: false
          }
        ],
        assignment: {
          title: "Assignment: Pass Three & Perfecting",
          instructions:
            "Submit at least 1 photo of a pair of brows after pass three. We want refinement, soft texture, and balanced density.",
          minFiles: 1,
          maxFiles: 6
        },
        quiz: {
          title: "Pass Three Check",
          questions: [
            {
              q: "Where does pass three build saturation first?",
              options: [
                "From the front to the tail only",
                "From the tail toward the middle to build finished density",
                "Only at the tail with no mid-brow work",
                "Only at the front to keep it bold"
              ],
              answerIndex: 1
            },
            {
              q: "A perfecting pass should feel:",
              options: [
                "Heavy and fast",
                "Controlled and selective, refining only where needed while keeping the front soft",
                "Only at the tail",
                "Random to fill gaps"
              ],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 8 — OMBRÉ TOUCH-UP STRATEGY
       */
      {
        id: "ombre_pc_8",
        index: 8,
        title: "Ombré Touch-Up Strategy",
        blocks: [
          {
            type: "text",
            text:
              "A healed first-session ombré brow should look softer and typically 1–2 shades lighter than the final goal. The touch-up is where you refine and finish density without harshness."
          },
          {
            type: "text",
            text:
              "Patchiness is commonly caused by unstable stretch, uneven pressure/rhythm, overlapping too aggressively, or healing variables (oily skin, poor aftercare, sun exposure, picking)."
          },
          {
            type: "text",
            text:
              "Correction: slow down, tighten your stretch, keep a consistent rhythm, and spot-correct only where needed with light layering—don’t blanket-pack the brow."
          }
        ],
        quiz: {
          title: "Touch-Up Check",
          questions: [
            {
              q: "How should a healed first session ombré brow look before touch-up?",
              options: [
                "Darker than the final result",
                "A shade or two lighter than the final desired result",
                "Only dark at the tail",
                "Uneven and patchy by default"
              ],
              answerIndex: 1
            },
            {
              q: "What is the best approach to patchy areas at touch-up?",
              options: [
                "Over-pack the entire brow",
                "Spot-correct with light, controlled layering",
                "Skip the touch-up",
                "Only darken the outline"
              ],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 9 — OMBRÉ STEP BY STEP
       */
      {
        id: "ombre_pc_9",
        index: 9,
        title: "Ombré Step-by-Step — Process Discipline",
        blocks: [
          {
            type: "text",
            text:
              "Follow your step-by-step process: map with precision, outline with control, build saturation in layers, and protect a soft front gradient."
          },
          {
            type: "text",
            text:
              "Quality standard: even saturation without striping, harsh blocks, or overworked edges. Reassess between passes—don’t mindlessly repeat motion."
          },
          {
            type: "resource",
            url: "https://cosmetinkacademyappuploads.s3.us-east-2.amazonaws.com/course_resources/67d8adb88d60558fe072ffa0/1769564765934.pdf",
            label: "Ombré Step-by-Step Guide (PDF)"
          }
        ],
        assignment: {
          title: "Assignment: Ombré Step-by-Step",
          instructions:
            "Submit 3 full pairs of ombré brows following your step-by-step process. In the text box, explain when you should numb and when you should do a pigment bath (based on the guide + skin response).",
          minFiles: 1,
          maxFiles: 6,
          requiresText: true
        },
        quiz: {
          title: "Readiness Check",
          questions: [
            {
              q: "What is the most important goal during layered ombré passes?",
              options: ["Heavy outline", "Even saturation and a soft gradient", "Fast completion time", "Maximum pressure"],
              answerIndex: 1
            },
            {
              q: "According to the step-by-step guide, when should you numb and pigment bath during ombré?",
              options: [
                "Only before mapping",
                "After the outline and between passes as needed, based on skin response",
                "Only after the final pass",
                "Never use numbing or pigment bath"
              ],
              answerIndex: 1
            },
            {
              q: "How long can numbing sit, and when is the exception?",
              options: [
                "Only after pass 1 it can sit until the next pass; otherwise 30 seconds or less",
                "It can sit 15–20 minutes at any time",
                "It should always sit at least 5 minutes",
                "It can sit as long as needed during every pass"
              ],
              answerIndex: 0
            },
            {
              q: "What is the purpose of a pigment bath in the step-by-step process?",
              options: [
                "To make the brow darker immediately",
                "To soothe the skin and support even saturation",
                "To replace a numbing step",
                "To outline the brow"
              ],
              answerIndex: 1
            }
          ]
        }
      }
    ]
  },

  lip_blush: {
    title: "Lip Blush — Post-Course",
    sections: [
      /**
       * SECTION 1 — LIP MAPPING
       */
      {
        id: "lip_pc_1",
        index: 1,
        title: "Lip Mapping — Shape, Symmetry, and Border Control",
        blocks: [
          {
            type: "text",
            text:
              "Great lip blush starts with clean, balanced mapping. Your map sets the final shape—so symmetry, cupid’s bow alignment, and a clean vermilion border are non-negotiable."
          },
          {
            type: "image",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/lipclassmaterials/LipMappingPic.jpg",
            alt: "Lip mapping demonstration"
          },
          {
            type: "text",
            text:
              "White paste technique: place a small amount on the back of your glove. Use an angled flat brush and press/stamp to create flat, crisp lines—don’t ‘paint’ thick strokes."
          },
          {
            type: "text",
            text:
              "Safety boundary: work within the visible lip tissue. Avoid mapping or tattooing on/above pores or onto facial skin outside the vermilion border."
          },
          {
            type: "text",
            text:
              "Map with lips at rest. Ask the client to relax and keep chin level—smiling or stretching distorts symmetry."
          },
          {
            type: "text",
            text:
              "Quality standard: crisp, even outline, corners meet cleanly, cupid’s bow is balanced, and the shape looks natural (not overdrawn)."
          }
        ],
        assignment: {
          title: "Assignment: Lip Mapping",
          instructions:
            "Upload at least 1 photo of mapped lips on a latex lip. We’re grading crisp inside-border lines, symmetry, and a natural, balanced shape.",
          minFiles: 1,
          maxFiles: 3
        },
        quiz: {
          title: "Mapping Check",
          questions: [
            {
              q: "What should lip mapping protect most?",
              options: ["A bold overdrawn outline", "Symmetry and natural shape", "Maximum saturation", "Faster appointment time"],
              answerIndex: 1
            },
            {
              q: "When should you correct asymmetry in the outline?",
              options: ["After shading", "Before any shading begins", "Only at the end", "After numbing"],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 2 — LIP OUTLINE TECHNIQUE
       */
      {
        id: "lip_pc_2",
        index: 2,
        title: "Lip Outline Technique — Clean, Soft Border",
        blocks: [
          {
            type: "text",
            text:
              "Outline goal: a clean, soft border—not a harsh lipstick line. Use a 1RL (.30mm), keep the needle at ~90°, maintain consistent stretch, and keep your speed controlled (your class standard: ~5.0 or lower)."
          },
          {
            type: "resource",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/lipclassmaterials/lipblushoutlinetechnique.pdf",
            label: "Lip Outline Technique (PDF)"
          },
          {
            type: "video",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/lipclassmaterials/outlininglip.mp4",
            alt: "Outlining the lip border",
            muted: true
          },
          {
            type: "text",
            text:
              "Start on the lower lip at center and work in short, flicking, overlapping motions toward you, moving gradually toward the oral commissure."
          },
          {
            type: "text",
            text:
              "Pressure should be light and consistent. Wipe in small sections to confirm the outline is taking. A soft outline blends better and heals more naturally."
          },
          {
            type: "video",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/lipclassmaterials/checkingoutlinelips.mp4",
            alt: "Checking the outline",
            muted: true
          },
          {
            type: "text",
            text:
              "To check the outline cleanly: use a damp lip wand and wipe from inside → outside. This pushes mess outward instead of smearing pigment inside the lip."
          },
          {
            type: "text",
            text:
              "Numbing note: after the outline is complete, apply secondary numbing. This is the only time during the lip procedure you leave numbing sitting longer than 30 seconds. You will complete the outline on the other lips while this sits."
          },
          {
            type: "text",
            text:
              "Cupid’s bow standard: stretch for a flat, taut surface. Move slowly, keep your line smooth, and recheck symmetry before continuing."
          },
          {
            type: "video",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/lipclassmaterials/outliningcupidsbow.mp4",
            alt: "Outlining the cupid’s bow",
            muted: true
          }
        ],
        assignment: {
          title: "Assignment: Lip Outline",
          instructions: "Submit photos of a fully outlined lip on latex (no shading yet). Include at least one close-up of cupid’s bow.",
          minFiles: 1,
          maxFiles: 6
        },
        quiz: {
          title: "Outline Check",
          questions: [
            {
              q: "Why must the outline be clean before shading?",
              options: ["It improves color retention", "It sets the final lip shape", "It speeds healing", "It reduces pigment use"],
              answerIndex: 1
            },
            {
              q: "What needle and angle should be used for outlining (training standard)?",
              options: ["3RL at 45°", "1RL (.30mm) at ~90°", "5RL at 30°", "1RS at 60°"],
              answerIndex: 1
            },
            {
              q: "Where should you start outlining and how should you move?",
              options: [
                "Upper lip center, long straight pulls outward",
                "Lower lip center, short back-and-forth overlapping motions toward you",
                "Corner to corner in one continuous pass",
                "Cupid’s bow only, then fill later"
              ],
              answerIndex: 1
            },
            {
              q: "What is the biggest risk of a shaky outline?",
              options: ["Faster healing", "An uneven final lip border", "Lighter color", "Less swelling"],
              answerIndex: 1
            },
            {
              q: "How should you wipe to check the outline without making the inside messy?",
              options: [
                "Outside to inside with a dry wipe",
                "Inside to outside with a damp lip wand",
                "Circular wiping over the entire lip",
                "Wipe only the cupid’s bow"
              ],
              answerIndex: 1
            },
            {
              q: "When is the only time during the lip procedure you can leave numbing sitting on the outline (per protocol)?",
              options: ["Before mapping", "After outlining", "After the final pass only", "During every pass"],
              answerIndex: 1
            },
            {
              q: "What is most important when outlining the cupid’s bow?",
              options: ["Fast strokes to save time", "A taut, flat surface with a good stretch", "Heavy pressure for crispness", "Skipping the center to keep it soft"],
              answerIndex: 1
            },
            {
              q: "What machine speed should you use for lip outlining (training standard)?",
              options: ["7.0 or higher", "5.0 or lower", "6.5 exactly", "Any speed, it doesn’t matter"],
              answerIndex: 1
            },
            {
              q: "Why wipe small sections while outlining?",
              options: ["To dry the lips completely", "To ensure the outline is taking and stays visible", "To remove all numbing", "To blend shading early"],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 3 — LIP BLUSH SHADING (ALL PASSES)
       */
      {
        id: "lip_pc_3",
        index: 3,
        title: "Lip Blush Shading — Whip + Cross-Hatch Control",
        blocks: [
          {
            type: "text",
            text:
              "Shading goal: smooth, even saturation with soft edges. Hold the machine upright (~90°). Keep your wrist lifted so the whip has “air time”—this helps the needle land and exit softly and prevents hard, dark spots."
          },
          {
            type: "video",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/lipclassmaterials/lipshading1.mp4",
            alt: "Lip shading example 1"
          },
          {
            type: "text",
            text:
              "Work in small sections. You should NOT see obvious start/stop marks. Vary stroke length and overlap intentionally to avoid striping."
          },
          {
            type: "video",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/lipclassmaterials/lipshading2.mp4",
            alt: "Lip shading example 2"
          },
          {
            type: "text",
            text:
              "This video shows two-direction whipping, but remember that you can also do just one-direction whipping: the needle contacts the skin only toward you—great for beginners and easier to control."
          },
          {
            type: "text",
            text:
              "Cross-hatching: used to fill and even saturation. Many artists blend whip + cross-hatch for the smoothest result."
          },
          {
            type: "text",
            text:
              "Overlap at least ~50% to prevent ‘zebra lines.’ Underlap = stripes."
          },
          {
            type: "text",
            text:
              "Adjust by zone: Center lip often needs more saturation for retention; outer lip skin is thinner and typically requires lighter pressure and less saturation, so spend more time (go more slowly through) the center of the lips."
          },
          {
            type: "resource",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/lipclassmaterials/lipblushtechniqueshading.pdf",
            label: "Lip Blush Shading Technique (PDF)"
          }
        ],
        assignment: {
          title: "Assignment: Shading Control (Latex)",
          instructions:
            "Submit 1–3 photos showing an even shaded lip on latex (not just outline). We’re grading smooth saturation, no striping, and soft edges.",
          minFiles: 1,
          maxFiles: 6
        },
        quiz: {
          title: "Shading Technique Check",
          questions: [
            {
              q: "Why should the needle land and exit softly during shading?",
              options: [
                "To create hard, dark spots",
                "To avoid hard, dark spots and keep saturation smooth",
                "To speed up the procedure",
                "To avoid stretching the lip"
              ],
              answerIndex: 1
            },
            {
              q: "What overlap percentage helps prevent zebra lines?",
              options: ["10%", "25%", "50% or more", "100%"],
              answerIndex: 2
            },
            {
              q: "Which statement about center vs. outer lips is correct?",
              options: [
                "Outer lip needs heavier pressure and more saturation",
                "Center needs more saturation; outer lip needs lighter pressure and less saturation",
                "Center needs less saturation; outer lip needs more saturation",
                "Both zones need equal pressure and saturation"
              ],
              answerIndex: 1
            },
            {
              q: "What is one-direction whipping?",
              options: [
                "Needle hits the skin only away from you",
                "Needle hits the skin only toward you",
                "Needle drags across the skin without lifting",
                "Needle stays in the skin the entire time"
              ],
              answerIndex: 1
            },
            {
              q: "What is cross-hatching used for?",
              options: ["To outline the lips", "To fill and even out saturation", "To replace stretching", "To increase needle depth"],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 4 — LIP BLUSH TOUCH-UPS
       */
      {
        id: "lip_pc_4",
        index: 4,
        title: "Lip Blush Touch-Ups — Refinement Without Overwork",
        blocks: [
          {
            type: "text",
            text:
              "Touch-ups refine the healed result. A first session should heal softer and slightly lighter than the final goal—giving room to build a second layer without heaviness."
          },
          {
            type: "text",
            text:
              "If patchy: spot-correct only where needed with light layering. Avoid blanket overpacking. When approaching, be sure your stretch is very good and your lips are taut and flat where you’re working. This will help avoid patchy healing."
          },
          {
            type: "text",
            text:
              "Reinforce pre-care/aftercare. Picking, sun exposure, smoking/vaping, dehydration, and inconsistent ointment use can reduce retention."
          }
        ],
        quiz: {
          title: "Touch-Up Check",
          questions: [
            {
              q: "How should a first session lip blush heal before touch-up?",
              options: ["Darker than the final result", "Soft and slightly lighter than the final result", "Very patchy and uneven", "Only dark at the corners"],
              answerIndex: 1
            },
            {
              q: "What is the best approach to patchy areas at touch-up?",
              options: ["Over-pack the entire lip", "Spot-correct with light, controlled layering", "Skip the touch-up", "Only darken the outline"],
              answerIndex: 1
            }
          ]
        }
      },

      /**
       * SECTION 5 — LIP NEUTRALIZATION (MELANIN-RICH LIPS)
       */
      {
        id: "lip_pc_5",
        index: 5,
        title: "Lip Neutralization — Melanin-Rich Lips",
        blocks: [
          {
            type: "image",
            url: "https://cosacademyassets.s3.us-east-2.amazonaws.com/lipclassmaterials/LipNeutralization.jpg",
            alt: "Lip neutralization undertones"
          },
          {
            type: "text",
            text:
              "Melanin-rich lips may show warm (brown) or cool (purple/blue) undertones. A reliable reference point is the center of the bottom lip—it’s typically the most naturally pink area."
          },
          {
            type: "text",
            text:
              "Neutralization mix: Step 1 base color matching center of bottom lip (10 drops). Step 2 add lightener (5 drops)—COSMETINK's Watermelon Blush for warm lift, or Marshmallow Whip for the coolest/brightest lift."
          },
          {
            type: "text",
            text:
              "Step 3 brightener: Cranberry Crush (2–5 drops). Darker lips typically need more brightener to counteract deeper undertones."
          },
          {
            type: "text",
            text:
              "Quality standard: your final mix should resemble an exaggerated version of that natural center lip tone—about 1–2 shades more vibrant—so it heals into a balanced result."
          }
        ],
        quiz: {
          title: "Neutralization Check",
          questions: [
            {
              q: "What area should you match when choosing the base color for neutralization?",
              options: ["Upper lip border", "Corners of the mouth", "Center of the bottom lip", "Cupid’s bow only"],
              answerIndex: 2
            },
            {
              q: "What is the 3-step mix ratio for neutralization (protocol)?",
              options: ["10 base + 5 lightener + 2–5 brightener", "10 base + 2 lightener + 5 brightener", "5 base + 10 lightener + 2 brightener", "Only base color, no lightener or brightener"],
              answerIndex: 0
            },
            {
              q: "Which brightener is used for dark lips in this protocol?",
              options: ["Marshmallow Whip", "Watermelon Blush", "Cranberry Crush", "Nude Peach"],
              answerIndex: 2
            }
          ]
        }
      },

      /**
       * SECTION 6 — FULL LIP BLUSH STEP-BY-STEP
       */
      {
        id: "lip_pc_6",
        index: 6,
        title: "Full Lip Blush — Step-by-Step Standard",
        blocks: [
          {
            type: "text",
            text:
              "Follow your step-by-step process from mapping → outline → shading passes → final refinement. The finished result should be smooth, balanced, and natural—never harsh."
          },
          {
            type: "resource",
            url: "https://cosmetinkacademyappuploads.s3.us-east-2.amazonaws.com/course_resources/67d8adb88d60558fe072ffa0/1769631155628.pdf",
            label: "Full Lip Blush Step-by-Step (PDF)"
          },
          {
            type: "text",
            text:
              "Quality standard: even saturation, soft edges, and a clean shape that respects the natural lip border."
          }
        ],
        assignment: {
          title: "Assignment: Full Lip Blush",
          instructions:
            "Submit a fully completed lip blush on latex. We’re grading clean shape, smooth blend, even saturation, and a finished, client-ready look.",
          minFiles: 1,
          maxFiles: 6
        },
        quiz: {
          title: "Readiness Check",
          questions: [
            {
              q: "What should the final lip blush look like?",
              options: ["Overpacked and harsh", "Smooth, soft, and balanced", "Only the outline", "Very dark at the center only"],
              answerIndex: 1
            },
            {
              q: "What detail most improves healed lip results?",
              options: ["Over-packing pigment", "Even saturation and soft edges", "Fast passes only", "Skipping outline refinement"],
              answerIndex: 1
            },
            {
              q: "What should guide your step-by-step lip blush process?",
              options: ["Speed and heavy pressure", "Clean mapping, soft blend, and even saturation", "Skipping outline checks", "Darker center only"],
              answerIndex: 1
            }
          ]
        }
      }
    ]
  }
};






// =========================
// Small shared helpers
// =========================
const formatDateUTC = (d) => {
  if (!d) return "";
  const dateObj = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dateObj.getTime())) return "";
  return dateObj.toLocaleDateString("en-US", { timeZone: "UTC" });
};

const formatDateLocal = (d) => {
  if (!d) return "";
  const dateObj = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dateObj.getTime())) return "";
  return dateObj.toLocaleDateString("en-US");
};

const formatDateTimeLocal = (d) => {
  if (!d) return "";
  const dateObj = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dateObj.getTime())) return "";
  return dateObj.toLocaleString("en-US");
};

// =========================
// COURSE DEFINITIONS (BY KEY)
// =========================
const ALL_PREP_COURSES = [
  { key: "nanoblading_hairstrokes", title: "Nano Brows – Prep Course", track: "Brows • Prep" },
  { key: "ombre_powder_brows", title: "Ombré Brows – Prep Course", track: "Brows • Prep" },
  { key: "lip_blush", title: "Lip Blush – Prep Course", track: "Lips • Prep" },
];

const resolveCourseFromKey = (key) => {
  if (!key) return null;
  const found = ALL_PREP_COURSES.find((c) => c.key === key);
  if (found) return found;
  return { key, title: key.replace(/_/g, " "), track: "Course" };
};

// Normalize student course keys from a user object
const getStudentCourseKeysFromUser = (user) => {
  if (!user) return [];

  if (Array.isArray(user.coursesEnrolled) && user.coursesEnrolled.length) {
    return user.coursesEnrolled.slice();
  }

  if (Array.isArray(user.enrolledCourses) && user.enrolledCourses.length) {
    const first = user.enrolledCourses[0];
    if (typeof first === "string") return user.enrolledCourses.slice();
    return user.enrolledCourses.map((c) => c && (c.key || c.courseKey)).filter(Boolean);
  }

  if (Array.isArray(user.enrollments) && user.enrollments.length) {
    return user.enrollments
      .map((e) => {
        const c = e?.course || {};
        return c.key || c.courseKey;
      })
      .filter(Boolean);
  }

  return [];
};

// Normalize teaching course keys for masters
const getTeachingCourseKeysFromUser = (user) => {
  if (!user) return [];

  if (Array.isArray(user.teachingCourses) && user.teachingCourses.length) {
    const first = user.teachingCourses[0];
    if (typeof first === "string") return user.teachingCourses.slice();
    return user.teachingCourses.map((c) => c && (c.key || c.courseKey)).filter(Boolean);
  }

  if (Array.isArray(user.coursesTeaching) && user.coursesTeaching.length) {
    const first = user.coursesTeaching[0];
    if (typeof first === "string") return user.coursesTeaching.slice();
    return user.coursesTeaching.map((c) => c && (c.key || c.courseKey)).filter(Boolean);
  }

  return [];
};

const getUserDisplayAddress = (user) => {
  if (!user) return null;
  const addr = user.address || user.currentAddress || user.mailingAddress || null;
  if (!addr) return null;

  const street = (addr.street || addr.address1 || "").trim();
  const street2 = (addr.street2 || addr.address2 || "").trim();
  const city = (addr.city || "").trim();
  const state = (addr.state || "").trim();
  const zip = (addr.zip || addr.postal || addr.postalCode || "").trim();

  const hasAny = !!(street || street2 || city || state || zip);
  if (!hasAny) return null;

  return { street, street2, city, state, zip };
};

/**
 * Backend rule intent:
 * - Brows: 10:45 AM / 1:45 PM
 * - Lip:  10:30 AM / 1:30 PM
 */
const MODEL_TIME_SLOTS_BY_COURSE_KEY = {
  nanoblading_hairstrokes: ["10:45 AM", "1:45 PM"],
  ombre_powder_brows: ["10:45 AM", "1:45 PM"],
  lip_blush: ["10:30 AM", "1:30 PM"],
};

const requiresModelForCourseKey = (key) => !!MODEL_TIME_SLOTS_BY_COURSE_KEY[key];

// Utility: validate schedule map for given keys
const validateScheduleForKeys = (scheduleMap, keys = []) => {
  const problems = [];
  (keys || []).forEach((k) => {
    const sched = scheduleMap?.[k] || {};
    if (!sched?.startDate) {
      problems.push({ key: k, type: "startDate" });
      return;
    }
    if (requiresModelForCourseKey(k) && !sched?.modelTimeSlot) {
      problems.push({ key: k, type: "modelTimeSlot" });
    }
  });
  return problems;
};

// Utility: create/update enrollment (staff route)
const createOrUpdateEnrollment = async ({ studentId, courseKey, startDate, modelTimeSlot }) => {
  return EnrollmentAPI.createOrUpdateEnrollment({
    studentId,
    courseKey,
    startDate,
    modelTimeSlot,
  });
};

const getGlobalBloodborneFromUser = (user) => {
  const bb = user?.bloodborneCert || null;

  if (!bb) {
    return { required: true, status: "needed", url: "", submittedAt: null };
  }

  const url = bb.url || "";
  const status = bb.status || (url ? "submitted" : "needed");
  const submittedAtRaw = bb.submittedAt || bb.updatedAt || null;

  return {
    required: true,
    status,
    url,
    submittedAt: submittedAtRaw ? new Date(submittedAtRaw) : null,
    fileName: bb.fileName || "",
  };
};

const getCourseKeyFromEnrollment = (enr) => {
  const c = enr?.course || {};
  return c.key || c.courseKey || enr?.courseKey || enr?.key || "";
};

const computeQuizAttemptStats = (enr) => {
  const progress = Array.isArray(enr?.progress) ? enr.progress : [];

  let totalAttempts = 0;
  let failedAttempts = 0;
  let passedSections = 0;

  let lastAttempt = null;

  const sections = progress
    .map((p) => {
      const attempts = Array.isArray(p?.attempts) ? p.attempts : [];
      const sectionObj = p?.section;
      const sectionId = typeof sectionObj === "string" ? sectionObj : String(sectionObj?._id || "");
      const sectionTitle = typeof sectionObj === "object" && sectionObj?.title ? sectionObj.title : "";

      totalAttempts += attempts.length;
      failedAttempts += attempts.filter((a) => !a?.isPerfect).length;
      if (p?.quizPassed) passedSections += 1;

      const latest = attempts.length ? attempts[attempts.length - 1] : null;

      if (latest?.submittedAt) {
        const t = new Date(latest.submittedAt).getTime();
        const cur = lastAttempt?.submittedAt ? new Date(lastAttempt.submittedAt).getTime() : 0;
        if (!cur || t > cur) {
          lastAttempt = { ...latest, sectionId };
        }
      }

      return {
        sectionId,
        sectionTitle,
        quizPassed: !!p?.quizPassed,
        attempts,
        lastAttempt: latest
          ? {
              submittedAt: latest.submittedAt,
              correctCount: latest.correctCount,
              total: latest.total,
              isPerfect: !!latest.isPerfect,
            }
          : null,
        failures: attempts.filter((a) => !a?.isPerfect).length,
      };
    })
    .sort((a, b) => {
      const aT = a.lastAttempt?.submittedAt ? new Date(a.lastAttempt.submittedAt).getTime() : 0;
      const bT = b.lastAttempt?.submittedAt ? new Date(b.lastAttempt.submittedAt).getTime() : 0;
      return bT - aT;
    });

  return { totalAttempts, failedAttempts, passedSections, lastAttempt, sections };
};

const Dashboard = ({
  currentUser,

  studentUnreadCount = 0,

  enrolledCourses,
  teachingCourses,
  masters,

  pendingStudents,
  pendingMasters,
  activeStudents,
  fetchAdminSummary,
  fetchPendingStudents,
  fetchPendingMasters,
  fetchActiveStudents,
  fetchActiveMasters,
  approveStudent,
  rejectStudent,

  approveMaster,
  fetchMasters,

  onAvatarUpload,
  onAssignUserToCourse,
  onRemoveUserFromCourse,

  threads = [],
  getMessageThreads,
  onSocketReceiveMessage,
  incrementThreadUnread,
  incrementStudentUnread,
  hydrateStudentMessages,

  enrollments = [],
  fetchMyEnrollments,

  fetchEnrollmentsForUser,
}) => {
  const fileInputRef = useRef(null);

  // SOCKET stability refs
  const socketRef = useRef(null);

  // UI state
  const [studentSearch, setStudentSearch] = useState("");
  const [masterSearch, setMasterSearch] = useState("");
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const normalizedRole = (currentUser?.role || "").toLowerCase();
  const isStudent = normalizedRole === "student";
  const isMaster = normalizedRole === "master";
  const isGrandMaster = normalizedRole === "grandmaster";
  const [apprenticeshipOpen, setApprenticeshipOpen] = useState(false); // start collapsed

  const [showCourseConfirm, setShowCourseConfirm] = useState(false);
  const [courseConfirmText, setCourseConfirmText] = useState("");
  const [pendingCourseCommit, setPendingCourseCommit] = useState(null);
  const [isCourseSaving, setIsCourseSaving] = useState(false);

const [coursesOpenMobile, setCoursesOpenMobile] = useState(false);
const [booting, setBooting] = useState(true);

const [postCourseOpen, setPostCourseOpen] = useState(false);
const [activePostCourseKey, setActivePostCourseKey] = useState("");
const [postCourseAttentionByEnrollmentId, setPostCourseAttentionByEnrollmentId] = useState({})
const [postCourseAttentionByStudentId, setPostCourseAttentionByStudentId] = useState({});


const enrollmentByCourseKey = useMemo(() => {
  const map = {};
  (enrollments || []).forEach((enr) => {
    const key = getCourseKeyFromEnrollment(enr);
    if (key) map[key] = enr;
  });
  return map;
}, [enrollments]);

const activePostEnrollment = activePostCourseKey ? enrollmentByCourseKey[activePostCourseKey] : null;
const activePostModule = activePostCourseKey ? POST_COURSE_MODULES_BY_KEY[activePostCourseKey] : null;

useEffect(() => {
  // flip off booting quickly so UI paints
  const t = setTimeout(() => setBooting(false), 250);
  return () => clearTimeout(t);
}, []);

useEffect(() => {
  document.body.classList.add("booting");
  requestAnimationFrame(() => {
    // allow 1-2 frames to paint
    setTimeout(() => document.body.classList.remove("booting"), 400);
  });
}, []);

useEffect(() => {
  console.time("dashboard-mount");
  return () => console.timeEnd("dashboard-mount");
}, []);

useEffect(() => {
  console.time("fetch-enrollments");
  fetchMyEnrollments().finally(() => console.timeEnd("fetch-enrollments"));
}, []);


  // =========================
  // APPRENTICESHIP MODE (Student)
  // =========================
  const [apprenticeship, setApprenticeship] = useState(null);
  const [apprenticeshipLoading, setApprenticeshipLoading] = useState(false);
  const [apprenticeshipErr, setApprenticeshipErr] = useState("");
  const [draftUploads, setDraftUploads] = useState({});
  const [apprenticeshipSavingKey, setApprenticeshipSavingKey] = useState("");

  const getRequiredCounts = useCallback((type) => {
    if (type === "brow") return { before: 3, after: 3 };
    if (type === "lip") return { before: 2, after: 2 };
    return { before: 0, after: 0 };
  }, []);

  const refreshMyApprenticeship = useCallback(async () => {
    setApprenticeshipErr("");
    setApprenticeshipLoading(true);
    try {
      const data = await ApprenticeshipAPI.fetchMyApprenticeship();
      setApprenticeship(data || null);
    } catch (e) {
      console.error(e);
      setApprenticeshipErr("Could not load apprenticeship data.");
    } finally {
      setApprenticeshipLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUser || !isStudent) return;
    refreshMyApprenticeship();
  }, [currentUser, isStudent, refreshMyApprenticeship]);

  // -------------------------
  // Apprenticeship status helpers
  // -------------------------
  const prettyStatus = useCallback((statusRaw) => {
    const s = String(statusRaw || "").toLowerCase();
    if (s === "approved") return "Approved";
    if (s === "submitted") return "Submitted";
    if (s === "changes_requested") return "Changes requested";
    if (s === "rejected") return "Rejected";
    if (s === "not_submitted" || !s) return "Not submitted";
    return s.replace(/_/g, " ");
  }, []);

  const statusPillClass = useCallback((statusRaw) => {
    const s = String(statusRaw || "").toLowerCase();
    if (s === "approved") return "dash-status-pill dash-status-pill-approved";
    if (s === "pending_review") return "dash-status-pill dash-status-pill-pending";
    if (s === "changes_requested") return "dash-status-pill dash-status-pill-rejected"; // treat as "not approved"
    if (s === "rejected") return "dash-status-pill dash-status-pill-rejected";
    if (s === "submitted") return "dash-status-pill dash-status-pill-pending"; // just in case older values exist
    return "dash-status-pill dash-status-pill-idle";
  }, []);

  // -------------------------
  // Apprenticeship draft helpers
  // draftUploads shape:
  // { "0:first": { before:[File], after:[File] }, ... }
  // -------------------------
  const draftKey = useCallback((index, session) => `${index}:${session}`, []);

  const getDraft = useCallback(
    (index, session) => {
      const key = draftKey(index, session);
      const d = draftUploads?.[key] || {};
      return {
        before: Array.isArray(d.before) ? d.before : [],
        after: Array.isArray(d.after) ? d.after : [],
      };
    },
    [draftUploads, draftKey]
  );

  const addFilesToDraft = useCallback((index, session, kind, fileList) => {
    if (!fileList || !fileList.length) return;
    const files = Array.from(fileList);

    setDraftUploads((prev) => {
      const next = { ...(prev || {}) };
      const key = `${index}:${session}`;
      const existing = next[key] || {};
      const existingArr = Array.isArray(existing[kind]) ? existing[kind] : [];
      next[key] = { ...existing, [kind]: [...existingArr, ...files] };
      return next;
    });
  }, []);

  const removeDraftFile = useCallback((index, session, kind, fileIndex) => {
    setDraftUploads((prev) => {
      const next = { ...(prev || {}) };
      const key = `${index}:${session}`;
      const existing = next[key] || {};
      const arr = Array.isArray(existing[kind]) ? [...existing[kind]] : [];
      if (fileIndex < 0 || fileIndex >= arr.length) return prev;
      arr.splice(fileIndex, 1);
      next[key] = { ...existing, [kind]: arr };
      return next;
    });
  }, []);

  const clearDraft = useCallback((index, session) => {
    setDraftUploads((prev) => {
      const next = { ...(prev || {}) };
      delete next[`${index}:${session}`];
      return next;
    });
  }, []);

  const defaultAdminCourseKey = useMemo(() => {
  return ALL_PREP_COURSES?.[0]?.key || "";
}, []);



  // gate: can create more case studies
  const canCreateMoreCaseStudies = useMemo(() => {
    const len = Array.isArray(apprenticeship?.caseStudies) ? apprenticeship.caseStudies.length : 0;
    return !!(apprenticeship?.unlocked && len < 3);
  }, [apprenticeship]);

  const hasApprenticeshipChangesRequested = useMemo(() => {
  const csArr = Array.isArray(apprenticeship?.caseStudies) ? apprenticeship.caseStudies : [];
  return csArr.some((cs) => {
    const first = String(cs?.firstSession?.status || "").toLowerCase();
    const second = String(cs?.secondSession?.status || "").toLowerCase();
    return first === "changes_requested" || second === "changes_requested";
  });
}, [apprenticeship]);




  // create next case study
  const createCaseStudy = useCallback(
    async (type) => {
      if (!isStudent) return;
      setApprenticeshipErr("");
      setApprenticeshipSavingKey("create-case");
      try {
        const fn = ApprenticeshipAPI.createCaseStudy;
        if (typeof fn !== "function") {
          throw new Error("ApprenticeshipAPI.createCaseStudy is not a function.");
        }
        await fn(type);
        await refreshMyApprenticeship();
      } catch (e) {
        console.error(e);
        setApprenticeshipErr("Could not create a new case study.");
      } finally {
        setApprenticeshipSavingKey("");
      }
    },
    [isStudent, refreshMyApprenticeship]
  );

  // submit a session for review
  const submitApprenticeshipSession = useCallback(
    async ({ index, session }) => {
      if (!isStudent) return;

      const caseStudies = Array.isArray(apprenticeship?.caseStudies) ? apprenticeship.caseStudies : [];
      const cs = caseStudies?.[index];

      if (!cs) {
        setApprenticeshipErr("That case study no longer exists. Please refresh and try again.");
        return;
      }

      const type = String(cs?.type || "").toLowerCase();
      const req = getRequiredCounts(type);

      if (!req || typeof req.before !== "number" || typeof req.after !== "number") {
        setApprenticeshipErr("Could not determine required photo counts for this case study.");
        return;
      }

      const d = getDraft(index, session) || {};
      const beforeFiles = Array.isArray(d.before) ? d.before : [];
      const afterFiles = Array.isArray(d.after) ? d.after : [];

      // ✅ front-end enforcement (matches your UI rules)
      if (beforeFiles.length !== req.before || afterFiles.length !== req.after) {
        setApprenticeshipErr(`Please upload exactly ${req.before} BEFORE and ${req.after} AFTER photos before submitting.`);
        return;
      }

      setApprenticeshipErr("");
      setApprenticeshipSavingKey(`${index}:${session}`);

      try {
        await ApprenticeshipAPI.submitSession({
          index,
          session,
          beforeFiles,
          afterFiles,
        });

        clearDraft(index, session);
        await refreshMyApprenticeship();
      } catch (e) {
        console.error(e);

        // ✅ show real backend error if available
        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          (typeof e?.message === "string" ? e.message : "") ||
          "Could not submit session for review.";

        setApprenticeshipErr(msg);
      } finally {
        setApprenticeshipSavingKey("");
      }
    },
    [
      isStudent,
      apprenticeship,
      getRequiredCounts,
      getDraft,
      clearDraft,
      refreshMyApprenticeship,
      setApprenticeshipErr,
      setApprenticeshipSavingKey,
    ]
  );

  const updateApprenticeshipSessionInfo = useCallback(
    async ({ index, session, clientName, sessionAt }) => {
      if (!isStudent) return;
      setApprenticeshipErr("");
      try {
        await ApprenticeshipAPI.updateSessionInfo({
          index,
          session,
          clientName,
          sessionAt,
        });
        await refreshMyApprenticeship();
      } catch (e) {
        console.error(e);
        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          (typeof e?.message === "string" ? e.message : "") ||
          "Could not save session info.";
        setApprenticeshipErr(msg);
      }
    },
    [isStudent, refreshMyApprenticeship, setApprenticeshipErr]
  );

  

  // =========================
  // GM / Enrollment panel state
  // =========================
  const [selectedStudentCourseKeys, setSelectedStudentCourseKeys] = useState([]);
  const [selectedMasterCourseKeys, setSelectedMasterCourseKeys] = useState([]);
  const [personEnrollmentsById, setPersonEnrollmentsById] = useState({});
  const [scheduleByPersonId, setScheduleByPersonId] = useState({});
  const [queuedCoursesByPersonId, setQueuedCoursesByPersonId] = useState({});
  const [pendingRemovalsByPersonId, setPendingRemovalsByPersonId] = useState({});
  const [pendingApprByStudentId, setPendingApprByStudentId] = useState({});
  const [photoPackageByStudentId, setPhotoPackageByStudentId] = useState({});

  const getEnrollmentsForSelectedPerson = useCallback(
    (person) => {
      const personId = String(person?._id || person?.id || "");
      if (!personId) return [];
      const cached = personEnrollmentsById?.[personId];
      return Array.isArray(cached) ? cached : [];
    },
    [personEnrollmentsById]
  );

  // ✅ IMPORTANT: do NOT reference gmStudentPool here (TDZ risk). Use props directly.
  const refreshPendingApprenticeshipBadges = useCallback(async () => {
    if (!isGrandMaster) return;

    const ids = [...(pendingStudents || []), ...(activeStudents || [])]
      .map((s) => String(s?._id || s?.id || ""))
      .filter(Boolean);

    const uniq = Array.from(new Set(ids));

    if (!uniq.length) {
      setPendingApprByStudentId({});
      return;
    }

    try {
      const summary = await ApprenticeshipAPI.fetchPendingSummary(uniq);
      setPendingApprByStudentId(summary || {});
    } catch (e) {
      console.error("fetchPendingSummary failed:", e);
    }
  }, [isGrandMaster, pendingStudents, activeStudents]);

  const refreshPhotoPackageBadges = useCallback(async () => {
    if (!isGrandMaster) return;

    const ids = [...(pendingStudents || []), ...(activeStudents || [])]
      .map((s) => String(s?._id || s?.id || ""))
      .filter(Boolean);

    const uniq = Array.from(new Set(ids));

    if (!uniq.length) {
      setPhotoPackageByStudentId({});
      return;
    }

    try {
      const res = await EnrollmentAPI.fetchPhotoPackageSummary(uniq);
      const map =
        res?.data?.byStudentId && typeof res.data.byStudentId === "object"
          ? res.data.byStudentId
          : {};
      setPhotoPackageByStudentId(map);
    } catch (e) {
      console.error("fetchPhotoPackageSummary failed:", e);
    }
  }, [isGrandMaster, pendingStudents, activeStudents]);

  useEffect(() => {
    if (!isGrandMaster) return;
    refreshPendingApprenticeshipBadges();
  }, [isGrandMaster, refreshPendingApprenticeshipBadges]);

  useEffect(() => {
    if (!isGrandMaster) return;
    refreshPhotoPackageBadges();
  }, [isGrandMaster, refreshPhotoPackageBadges]);

  // profile derived
  const fullName =
    currentUser?.fullName ||
    `${currentUser?.firstName || currentUser?.first_name || ""} ${currentUser?.lastName || currentUser?.last_name || ""}`.trim() ||
    currentUser?.username ||
    currentUser?.email ||
    "Artist";

  const avatarUrl =
    currentUser?.profilePicUrl || currentUser?.avatarUrl || currentUser?.imageUrl || currentUser?.profilePic || null;

  const messagesPath = isStudent ? "/messages" : "/admin/messages";

  const headerTagline = (() => {
    if (isStudent) return "STUDENT DASHBOARD";
    if (isMaster && !isGrandMaster) return "MASTER STUDIO PANEL";
    if (isGrandMaster) return "GRANDMASTER ADMIN";
    return "COSMETINK ACADEMY";
  })();

  const getRoleLabel = () => {
    if (isStudent) return "Student Artist";
    if (isMaster && !isGrandMaster) return "Master Artist";
    if (isGrandMaster) return "Grandmaster";
    return "Artist";
  };

  // instructor
  const instructor = currentUser?.instructor || currentUser?.master || currentUser?.assignedMaster || null;
  const instructorName =
    instructor?.fullName ||
    `${instructor?.firstName || instructor?.first_name || ""} ${instructor?.lastName || instructor?.last_name || ""}`.trim() ||
    "Your Instructor";

  const instructorAvatar =
    instructor?.profilePicUrl || instructor?.avatarUrl || instructor?.imageUrl || instructor?.profilePic || null;

  // unread
  const totalThreadUnread = useMemo(() => {
    if (!Array.isArray(threads) || !threads.length) return 0;
    return threads.reduce((sum, t) => sum + (t?.unreadCount || 0), 0);
  }, [threads]);

  const hasUnreadMessages = isStudent ? studentUnreadCount > 0 : totalThreadUnread > 0;

  // Student: fetch enrollments
  useEffect(() => {
    if (!currentUser || !isStudent || !fetchMyEnrollments) return;
    fetchMyEnrollments();
  }, [currentUser, isStudent, fetchMyEnrollments]);

  // GM: load admin data
  useEffect(() => {
    if (!isGrandMaster) return;
    fetchAdminSummary && fetchAdminSummary();
    fetchPendingStudents && fetchPendingStudents();
    fetchPendingMasters && fetchPendingMasters();
    fetchMasters && fetchMasters();
    fetchActiveStudents && fetchActiveStudents();
    fetchActiveMasters && fetchActiveMasters();
  }, [
    isGrandMaster,
    fetchAdminSummary,
    fetchPendingStudents,
    fetchPendingMasters,
    fetchMasters,
    fetchActiveStudents,
    fetchActiveMasters,
  ]);

  // Student: hydrate messages
  useEffect(() => {
    if (!currentUser || !isStudent || !hydrateStudentMessages) return;
    hydrateStudentMessages();
  }, [currentUser, isStudent, hydrateStudentMessages]);

  // SOCKET: message/new + enrollment/apprenticeship updates
  useEffect(() => {
    if (!currentUser || !onSocketReceiveMessage) return;

    const socket = io({ withCredentials: true });
    socketRef.current = socket;

    const refreshSelectedPersonEnrollments = async (studentId) => {
      if (!studentId || !selectedPerson || !fetchEnrollmentsForUser) return;
      const selectedId = String(selectedPerson?._id || selectedPerson?.id || "");
      if (!selectedId || selectedId !== String(studentId)) return;

      try {
        const res = await fetchEnrollmentsForUser(selectedId);
        const enrollmentsArr = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.enrollments)
          ? res.enrollments
          : [];

        setPersonEnrollmentsById((prev) => ({ ...(prev || {}), [String(selectedId)]: enrollmentsArr }));
      } catch (e) {
        console.warn("refreshSelectedPersonEnrollments failed:", e);
      }
    };

    socket.on("postcourse:submitted", (payload) => {
      const enrollmentId = String(payload?.enrollmentId || "");
      const studentId = String(payload?.studentId || "");

      if (isStudent) {
        const currentId = String(currentUser?._id || currentUser?.id || "");
        if (studentId && currentId && studentId === currentId && enrollmentId) {
          setPostCourseAttentionByEnrollmentId((prev) => ({ ...(prev || {}), [enrollmentId]: true }));
          fetchMyEnrollments && fetchMyEnrollments();
        }
      }

      if (normalizedRole === "master" || normalizedRole === "grandmaster") {
        if (studentId) {
          setPostCourseAttentionByStudentId((prev) => ({ ...(prev || {}), [studentId]: true }));
          refreshSelectedPersonEnrollments(studentId);
          fetchAdminSummary && fetchAdminSummary();
        }
      }
    });

    socket.on("postcourse:submitted:student", (payload) => {
      const enrollmentId = String(payload?.enrollmentId || "");
      const studentId = String(payload?.studentId || "");

      if (isStudent) {
        const currentId = String(currentUser?._id || currentUser?.id || "");
        if (studentId && currentId && studentId === currentId && enrollmentId) {
          setPostCourseAttentionByEnrollmentId((prev) => ({ ...(prev || {}), [enrollmentId]: true }));
          fetchMyEnrollments && fetchMyEnrollments();
        }
      }
    });



  const handlePostCourseReviewed = (payload) => {
    const enrollmentId = String(payload?.enrollmentId || "");
    const studentId = String(payload?.studentId || "");
    const action = String(payload?.action || "").toLowerCase(); // approve | request_changes

    if (isStudent && enrollmentId) {
      setPostCourseAttentionByEnrollmentId((prev) => {
        const next = { ...(prev || {}) };
        if (action === "approve") delete next[enrollmentId];
        if (action === "request_changes") next[enrollmentId] = true;
        return next;
      });
      fetchMyEnrollments && fetchMyEnrollments();
    }

    if ((normalizedRole === "master" || normalizedRole === "grandmaster") && studentId) {
      // staff badge should clear because "submitted" is gone after review
      setPostCourseAttentionByStudentId((prev) => {
        const next = { ...(prev || {}) };
        delete next[studentId];
        return next;
      });

      refreshSelectedPersonEnrollments(studentId);
      fetchAdminSummary && fetchAdminSummary();
    }
  };

    socket.on("postcourse:reviewed", handlePostCourseReviewed);
    socket.on("postcourse:reviewed:staff", handlePostCourseReviewed);
    socket.on("postcourse:reviewed:student", handlePostCourseReviewed);

    socket.on("user:pending", (payload) => {
      if (normalizedRole !== "master" && normalizedRole !== "grandmaster") return;

      const role = String(payload?.role || "").toLowerCase();
      if (role === "master") {
        fetchPendingMasters && fetchPendingMasters();
      } else {
        fetchPendingStudents && fetchPendingStudents();
      }

      fetchAdminSummary && fetchAdminSummary();
    });



socket.on("apprenticeship:updated", (payload) => {
  const studentId = String(payload?.studentId || "");

  // ✅ Student: only refresh my panel when MY id matches
  if (isStudent) {
    const currentId = String(currentUser?._id || currentUser?.id || "");
    if (studentId && currentId && studentId === currentId) {
      refreshMyApprenticeship();
    }
    return;
  }






  // ✅ Staff: refresh badges
  if (normalizedRole === "master" || normalizedRole === "grandmaster") {
    refreshPendingApprenticeshipBadges();
  }
});

socket.on("photo-package:updated", (payload) => {
  const studentId = String(payload?.studentId || "");

  if (isStudent) {
    const currentStudentId = String(currentUser?._id || currentUser?.id || "");
    if (studentId && currentStudentId && studentId === currentStudentId) {
      fetchMyEnrollments && fetchMyEnrollments();
    }
    return;
  }

  if (normalizedRole === "master" || normalizedRole === "grandmaster") {
    refreshPhotoPackageBadges();
    refreshSelectedPersonEnrollments(studentId);
  }
});

socket.on("photo-package:opted-in", (payload) => {
  if (normalizedRole !== "master" && normalizedRole !== "grandmaster") return;

  refreshPhotoPackageBadges();
  refreshSelectedPersonEnrollments(payload?.studentId);

  const studentName = payload?.studentName || "A student";
  const courseTitle = payload?.courseTitle || "their course";
  window.alert(`${studentName} opted in for the photo package for ${courseTitle}.`);
});

socket.on("photo-package:ready", (payload) => {
  if (!isStudent) return;

  const studentId = String(payload?.studentId || "");
  const currentStudentId = String(currentUser?._id || currentUser?.id || "");
  if (!studentId || !currentStudentId || studentId !== currentStudentId) return;

  fetchMyEnrollments && fetchMyEnrollments();
  window.alert(payload?.message || "Your photo package is now ready!");
});

const currentId = String(currentUser?._id || currentUser?.id || "");
if (currentId) socket.emit("join", { userId: currentId, role: normalizedRole });




    socket.on("message:new", (msg) => {
      const currentId = currentUser._id || currentUser.id;

      const sender = msg.sender;
      const recipient = msg.recipient;

      const senderId = typeof sender === "string" ? sender : sender?._id || sender?.id;
      const recipientId = typeof recipient === "string" ? recipient : recipient?._id || recipient?.id;

      const isForMe = senderId === String(currentId) || recipientId === String(currentId);
      if (!isForMe) return;

      onSocketReceiveMessage(msg);

      const isToMe = recipientId === String(currentId);
      if (!isToMe) return;

      const isMasterRole = normalizedRole === "master" || normalizedRole === "grandmaster";
      const isStudentRole = normalizedRole === "student";

      const threadId = typeof msg.thread === "string" ? msg.thread : msg.thread?._id;

      if (isMasterRole) {
        if (!threadId) return;
        const existingThread = threads.find((t) => String(t._id) === String(threadId));
        if (existingThread) {
          incrementThreadUnread && incrementThreadUnread(threadId);
        } else if (getMessageThreads) {
          getMessageThreads();
        }
      }

      if (isStudentRole && incrementStudentUnread) {
        incrementStudentUnread();
      }
    });

    socket.on("enrollment:updated", (payload) => {
      const currentId = String(currentUser._id || currentUser.id || "");
      const userId = String(payload?.userId || payload?.studentId || payload?._id || "");
      if (!currentId || !userId) return;
      if (currentId !== userId) return;
      if (fetchMyEnrollments) fetchMyEnrollments();
    });

  socket.on("postcourse:completed", (payload) => {
    if (!currentId) return;
    if (String(payload?.userId || "") !== currentId) return;

    // ✅ optional: show confetti/modal
    // setPostCourseCongratsOpen(true);

    // you already refresh on enrollment:updated, but harmless:
    fetchMyEnrollments && fetchMyEnrollments();
  });
    
    

    return () => socket.disconnect();
  }, [
    currentUser,
    normalizedRole,
    threads,
    onSocketReceiveMessage,
    incrementThreadUnread,
    incrementStudentUnread,
    getMessageThreads,
    fetchMyEnrollments,
    refreshMyApprenticeship,
    refreshPendingApprenticeshipBadges,
    refreshPhotoPackageBadges,
    isStudent,
    selectedPerson,
    fetchEnrollmentsForUser,
    fetchAdminSummary,
    fetchPendingStudents,
    fetchPendingMasters,
  ]);

  

  // Master / GM: threads list
  useEffect(() => {
    if (!currentUser || !getMessageThreads) return;
    if (normalizedRole !== "master" && normalizedRole !== "grandmaster") return;
    getMessageThreads();
  }, [currentUser, normalizedRole, getMessageThreads]);

  // GM pools / filters
  const gmMasters = masters || currentUser?.masters || [];
  const gmPendingStudents = pendingStudents || currentUser?.pendingStudents || [];
  const gmPendingMasters = pendingMasters || currentUser?.pendingMasters || [];
  const gmCourses = ALL_PREP_COURSES;

  const gmStudentPool = useMemo(() => {
    const seen = new Set();
    const combined = [];

    (gmPendingStudents || []).forEach((s) => {
      const id = s._id || s.id;
      if (!id || seen.has(id)) return;
      seen.add(id);
      combined.push({ ...s, _isPendingStudent: true });
    });

    (activeStudents || []).forEach((s) => {
      const id = s._id || s.id;
      if (!id || seen.has(id)) return;
      seen.add(id);
      combined.push({ ...s, _isPendingStudent: s._isPendingStudent || false });
    });

    const lastMessageByStudentId = {};
    (threads || []).forEach((thread) => {
      const student = thread.student;
      const studentId = typeof student === "string" ? student : student?._id || student?.id;
      if (!studentId) return;

      const ts = thread.updatedAt ? new Date(thread.updatedAt).getTime() : 0;
      if (!lastMessageByStudentId[studentId] || ts > lastMessageByStudentId[studentId]) {
        lastMessageByStudentId[studentId] = ts;
      }
    });

    const createdTs = (u) => {
      const raw = u?.createdAt || u?.created_at || u?.created;
      const t = raw ? new Date(raw).getTime() : 0;
      return Number.isFinite(t) ? t : 0;
    };

    combined.sort((a, b) => {
      const aPending = !!(a._isPendingStudent || a.isApproved === false || a.status === "pending");
      const bPending = !!(b._isPendingStudent || b.isApproved === false || b.status === "pending");

      if (aPending !== bPending) return aPending ? -1 : 1;
      if (aPending && bPending) return createdTs(b) - createdTs(a);

      const aId = a._id || a.id;
      const bId = b._id || b.id;
      const aMsg = lastMessageByStudentId[aId] || 0;
      const bMsg = lastMessageByStudentId[bId] || 0;

      if (aMsg !== bMsg) return bMsg - aMsg;
      return createdTs(b) - createdTs(a);
    });

    return combined;
  }, [gmPendingStudents, activeStudents, threads]);

  const gmMasterPool = useMemo(() => {
    const seen = new Set();
    const combined = [];

    (gmPendingMasters || []).forEach((m) => {
      const id = m._id || m.id;
      if (!id || seen.has(id)) return;
      seen.add(id);
      combined.push({ ...m, _isPendingMaster: true });
    });

    (gmMasters || []).forEach((m) => {
      const id = m._id || m.id;
      if (!id || seen.has(id)) return;
      seen.add(id);
      combined.push({ ...m, _isPendingMaster: m._isPendingMaster || false });
    });

    const createdTs = (u) => {
      const raw = u?.createdAt || u?.created_at || u?.created;
      const t = raw ? new Date(raw).getTime() : 0;
      return Number.isFinite(t) ? t : 0;
    };

    combined.sort((a, b) => {
      const aPending = !!(a._isPendingMaster || a.isApproved === false || a.status === "pending");
      const bPending = !!(b._isPendingMaster || b.isApproved === false || b.status === "pending");
      if (aPending !== bPending) return aPending ? -1 : 1;
      if (aPending && bPending) return createdTs(b) - createdTs(a);

      const aFirst = (a.firstName || a.first_name || "").toLowerCase();
      const bFirst = (b.firstName || b.first_name || "").toLowerCase();
      if (aFirst !== bFirst) return aFirst.localeCompare(bFirst);
      const aLast = (a.lastName || a.last_name || "").toLowerCase();
      const bLast = (b.lastName || b.last_name || "").toLowerCase();
      return aLast.localeCompare(bLast);
    });

    return combined;
  }, [gmPendingMasters, gmMasters]);

  const filteredStudents = useMemo(() => {
    const term = studentSearch.trim().toLowerCase();
    if (!term) return gmStudentPool;

    return gmStudentPool.filter((s) => {
      const first = s.firstName || s.first_name || "";
      const last = s.lastName || s.last_name || "";
      const email = s.email || "";
      const full = `${first} ${last}`.toLowerCase();
      return full.includes(term) || email.toLowerCase().includes(term);
    });
  }, [gmStudentPool, studentSearch]);

  const filteredMasters = useMemo(() => {
    const term = masterSearch.trim().toLowerCase();
    if (!term) return gmMasterPool;

    return gmMasterPool.filter((m) => {
      const first = m.firstName || m.first_name || "";
      const last = m.lastName || m.last_name || "";
      const email = m.email || "";
      const full = `${first} ${last}`.toLowerCase();
      return full.includes(term) || email.toLowerCase().includes(term);
    });
  }, [gmMasterPool, masterSearch]);

  // Student courses (derived)
  const studentCourses = useMemo(() => {
    if (Array.isArray(enrollments) && enrollments.length) {
      return enrollments.map((enr) => {
        const course = enr.course || {};
        const key = course.key || course.courseKey;
        const postCourseProgress = Array.isArray(enr.postCourseProgress) ? enr.postCourseProgress : [];
const hasPostSubmitted = postCourseProgress.some((p) => String(p?.status || "").toLowerCase() === "submitted");
const hasPostChanges = postCourseProgress.some((p) => String(p?.status || "").toLowerCase() === "changes_requested");

        const baseMeta = resolveCourseFromKey(key);
        const startDate = enr.startDate ? new Date(enr.startDate) : null;
        const expiryDate = enr.expiryDate ? new Date(enr.expiryDate) : null;
        const now = new Date();

        const hasStarted = !!startDate && now >= startDate;
        const prepCompleted = !!enr.prepCompleted;
        const mainUnlocked = hasStarted && prepCompleted;

        const rawProgress = enr.sectionProgress || enr.progress || course.sectionProgress || {};
        const progressPercent =
          typeof rawProgress.percent === "number"
            ? rawProgress.percent
            : typeof enr.progressPercent === "number"
            ? enr.progressPercent
            : prepCompleted
            ? 100
            : 0;

        const currentSectionTitle =
          rawProgress.currentSectionTitle ||
          rawProgress.currentSection ||
          enr.currentSectionTitle ||
          enr.currentSectionName ||
          null;

        return {
          key,
          title: course.title || baseMeta.title,
          track: baseMeta.track,
          startDate,
          expiryDate,
          status: enr.status,
          prepCompleted,
          hasStarted,
          mainUnlocked,
          enrollmentId: enr._id,
          progressPercent,
          currentSectionTitle,
            // ✅ POST-COURSE COMPLETION (this is where your lines go)
  postCourseCompleted: !!enr.postCourseCompleted,
  postCourseCompletedAt: enr.postCourseCompletedAt ? new Date(enr.postCourseCompletedAt) : null,
  postCourseNeedsAttention: !!postCourseAttentionByEnrollmentId[String(enr._id)],
  postCourseProgress,
postCourseHasSubmitted: hasPostSubmitted,
postCourseHasChangesRequested: hasPostChanges,


        };
      });
    }

    if (Array.isArray(enrolledCourses) && enrolledCourses.length) {
      const first = enrolledCourses[0];
      if (typeof first === "string") {
        return enrolledCourses
          .map(resolveCourseFromKey)
          .filter(Boolean)
          .map((c) => ({
            ...c,
            progressPercent: 0,
            currentSectionTitle: null,
            hasStarted: false,
            mainUnlocked: false,
          }));
      }
      return enrolledCourses;
    }



    const keysFromUser = getStudentCourseKeysFromUser(currentUser);
    return keysFromUser
      .map(resolveCourseFromKey)
      .filter(Boolean)
      .map((c) => ({
        ...c,
        progressPercent: 0,
        currentSectionTitle: null,
        hasStarted: false,
        mainUnlocked: false,
      }));
  }, [enrollments, enrolledCourses, currentUser]);



const courseStartDatesByKey = useMemo(() => {
  const map = {};
  (studentCourses || []).forEach((c) => {
    if (!c?.key) return;
    if (!c?.startDate) return;
    map[c.key] = c.startDate; // Date object
  });
  return map;
}, [studentCourses]);


  
  const deleteCaseStudy = useCallback(
    async (index) => {
      if (!isStudent) return;

      setApprenticeshipErr("");
      setApprenticeshipSavingKey(`delete-case:${index}`);

      try {
        await ApprenticeshipAPI.deleteCaseStudy(index);
        await refreshMyApprenticeship();
      } catch (e) {
        console.error(e);
        setApprenticeshipErr("Could not remove that case study.");
      } finally {
        setApprenticeshipSavingKey("");
      }
    },
    [isStudent, refreshMyApprenticeship]
  );

  

  // Master courses (derived)
  const masterCourses = useMemo(() => {
    if (Array.isArray(teachingCourses) && teachingCourses.length) {
      const first = teachingCourses[0];
      if (typeof first === "string") return teachingCourses.map(resolveCourseFromKey).filter(Boolean);
      return teachingCourses;
    }
    const keysFromUser = getTeachingCourseKeysFromUser(currentUser);
    return keysFromUser.map(resolveCourseFromKey).filter(Boolean);
  }, [teachingCourses, currentUser]);

  // Avatar handlers
  const triggerAvatarPicker = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const userId = currentUser?._id || currentUser?.id;
    if (!userId) return;

    onAvatarUpload && onAvatarUpload(userId, file);
    e.target.value = "";
  };

  // Person open/close (GM panel)
  const getPersonId = (p) => String(p?._id || p?.id || "");

  const getScheduleMapForPerson = (p) => {
    const pid = getPersonId(p);
    if (!pid) return {};
    return scheduleByPersonId?.[pid] || {};
  };

  const ensureDefaultsForPersonAndKeys = (person, keys) => {
    const pid = getPersonId(person);
    if (!pid) return;

    setScheduleByPersonId((prev) => {
      const next = { ...(prev || {}) };
      const current = next[pid] ? { ...next[pid] } : {};

      (keys || []).slice(0, 3).forEach((k) => {
        if (!current[k]) {
          current[k] = {
            startDate: "",
            modelTimeSlot: requiresModelForCourseKey(k) ? (MODEL_TIME_SLOTS_BY_COURSE_KEY[k]?.[0] || "") : "",
          };
        } else if (requiresModelForCourseKey(k) && !current[k].modelTimeSlot) {
          current[k] = { ...(current[k] || {}), modelTimeSlot: MODEL_TIME_SLOTS_BY_COURSE_KEY[k]?.[0] || "" };
        }
      });

      next[pid] = current;
      return next;
    });
  };

  const openPerson = async (person) => {
    setSelectedPerson(person);

    const personRole = (person?.role || "").toLowerCase();
    const isPending =
      personRole === "student"
        ? !!(person?._isPendingStudent || person?.isApproved === false || person?.status === "pending")
        : personRole === "master"
        ? !!(person?._isPendingMaster || person?.isApproved === false || person?.status === "pending")
        : false;

    const personId = person?._id || person?.id;
    const pid = String(personId || "");

    if (pid) {
      setQueuedCoursesByPersonId((prev) => ({ ...(prev || {}), [pid]: [] }));
      setPendingRemovalsByPersonId((prev) => ({ ...(prev || {}), [pid]: [] }));
    }

    const isApprovedStudent = personRole === "student" && !isPending;

    if (personId && isApprovedStudent && fetchEnrollmentsForUser) {
      try {
        const res = await fetchEnrollmentsForUser(personId);

        const enrollmentsArr = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.enrollments)
          ? res.enrollments
          : [];

        setPersonEnrollmentsById((prev) => ({ ...(prev || {}), [String(personId)]: enrollmentsArr }));

        const keysFromEnrollments = enrollmentsArr.map(getCourseKeyFromEnrollment).filter(Boolean);
        ensureDefaultsForPersonAndKeys(person, keysFromEnrollments);

        setSelectedStudentCourseKeys([]);
        setSelectedMasterCourseKeys([]);
        return;
      } catch (e) {
        console.error("fetchEnrollmentsForUser failed:", e);
      }
    }

    

    const currentKeys =
      personRole === "student"
        ? getStudentCourseKeysFromUser(person)
        : personRole === "master"
        ? getTeachingCourseKeysFromUser(person)
        : [];

    ensureDefaultsForPersonAndKeys(person, currentKeys);

    if (isPending) {
      if (personRole === "student") {
        setSelectedStudentCourseKeys([]);
        setSelectedMasterCourseKeys([]);
      } else if (personRole === "master") {
        setSelectedMasterCourseKeys(currentKeys.slice(0, 3));
        setSelectedStudentCourseKeys([]);
      } else {
        setSelectedStudentCourseKeys([]);
        setSelectedMasterCourseKeys([]);
      }
      return;
    }

    setSelectedStudentCourseKeys([]);
    setSelectedMasterCourseKeys([]);
  };

  

  const handleClosePerson = () => {
    const pid = getPersonId(selectedPerson);


    setSelectedPerson(null);
    setSelectedStudentCourseKeys([]);
    setSelectedMasterCourseKeys([]);

    if (pid) {
      setQueuedCoursesByPersonId((prev) => ({ ...(prev || {}), [pid]: [] }));
      setPendingRemovalsByPersonId((prev) => ({ ...(prev || {}), [pid]: [] }));
    }
  };

    useEffect(() => {
  if (hasApprenticeshipChangesRequested) setApprenticeshipOpen(true);
}, [hasApprenticeshipChangesRequested]);



// ✅ After lists refresh, swap selectedPerson to the newest copy from the pools
const refreshSelectedPersonRef = useCallback(
  (userId) => {
    const idStr = String(userId || "");
    if (!idStr) return;

    const findById = (arr) =>
      (arr || []).find((u) => String(u?._id || u?.id || "") === idStr) || null;

    // priority: active first (approved), then pending
    const newest =
      findById(activeStudents) ||
      findById(gmMasters) || // optional if you want masters too
      findById(pendingStudents) ||
      findById(pendingMasters);

    if (newest) {
      // preserve local flags if needed, but clear pending if they’re now active
      setSelectedPerson((prev) => {
        const prevId = String(prev?._id || prev?.id || "");
        if (prevId !== idStr) return prev;
        return newest;
      });
    }
  },
  [activeStudents, pendingStudents, pendingMasters, gmMasters]
);

useEffect(() => {
  const id = selectedPerson?._id || selectedPerson?.id;
  if (!id) return;
  refreshSelectedPersonRef(id);
}, [activeStudents, pendingStudents, pendingMasters, gmMasters, selectedPerson, refreshSelectedPersonRef]);



  // If not logged in
  if (!currentUser) {
    return (
      <div className="dash-shell dash-shell-centered">
        <h2>COSMETINK ACADEMY</h2>
        <p>Please log in to see your dashboard.</p>
      </div>
    );
  }

  const isApprenticeshipCertificationEarned = (doc) => {
  if (!doc?.unlocked) return false;
  const csArr = Array.isArray(doc?.caseStudies) ? doc.caseStudies : [];
  if (csArr.length !== 3) return false;

  return csArr.every((cs) => {
    const first = String(cs?.firstSession?.status || "").toLowerCase();
    const second = String(cs?.secondSession?.status || "").toLowerCase();
    return first === "approved" && second === "approved";
  });
};


  

  return (
    <div className="dash-shell">

      {booting ? (
  <div className="dash-skeleton">
    <div className="dash-skel-card" />
    <div className="dash-skel-card" />
    <div className="dash-skel-card" />
  </div>
) : ( <>



      <DashboardHeader
        headerTagline={headerTagline}
        fullName={fullName}
        messagesPath={messagesPath}
        hasUnreadMessages={hasUnreadMessages}
        avatarUrl={avatarUrl}
        onOpenProfile={() => setIsProfileOpen(true)}
        fileInputRef={fileInputRef}
        certificationEarned={isApprenticeshipCertificationEarned(apprenticeship)}
        onAvatarChange={handleAvatarChange}
      />

      <main className="dash-main">
        <section className="dash-column dash-column-main">
          {isStudent && (
            <>
              <InstructorPanel
                instructor={instructor}
                instructorName={instructorName}
                instructorAvatar={instructorAvatar}
                studentUnreadCount={studentUnreadCount}
              />

              <PhotoPackageReadyPanel enrollments={enrollments} />

              <CourseListPanel
                title="My Courses"
                subtitle="Complete your prep work before class, then unlock course work and apprenticeship mode."
                courses={studentCourses}
                emptyText="You’re not enrolled in any courses yet. Once your master or grandmaster approves you and assigns a class, your courses will appear here."
                formatDateUTC={formatDateUTC}
                postCourseAttentionByEnrollmentId={postCourseAttentionByEnrollmentId}
                postCourseModulesByKey={POST_COURSE_MODULES_BY_KEY}
                allowExtensionPurchaseLink
              />

<StudentResourcesPanelContainer courseStartDatesByKey={courseStartDatesByKey} />
              <ProShopCard />

              <ApprenticeshipPanel
                apprenticeship={apprenticeship}
                apprenticeshipLoading={apprenticeshipLoading}
                apprenticeshipErr={apprenticeshipErr}
                apprenticeshipSavingKey={apprenticeshipSavingKey}
                canCreateMoreCaseStudies={canCreateMoreCaseStudies}
                getRequiredCounts={getRequiredCounts}
                prettyStatus={prettyStatus}
                statusPillClass={statusPillClass}
                createNextCaseStudy={createCaseStudy}
                submitApprenticeshipSession={submitApprenticeshipSession}
                updateApprenticeshipSessionInfo={updateApprenticeshipSessionInfo}
                getDraft={getDraft}
                addFilesToDraft={addFilesToDraft}
                removeDraftFile={removeDraftFile}
                deleteCaseStudy={deleteCaseStudy}
                isOpen={apprenticeshipOpen}
                onToggle={() => setApprenticeshipOpen((v) => !v)}
                hasChangesRequested={hasApprenticeshipChangesRequested}
              />

      
         

            </>
          )}

          

    {isGrandMaster && defaultAdminCourseKey ? (
  <CourseResourcesAdminContainer courseKey={defaultAdminCourseKey} />
) : null}

{isGrandMaster && (
  <div className={`dash-mobile-collapsible ${coursesOpenMobile ? "open" : ""}`}>
    <button
      type="button"
      className="dash-mobile-collapsible-trigger"
      onClick={() => setCoursesOpenMobile(v => !v)}
      aria-expanded={coursesOpenMobile}
    >
      <span className="dash-mobile-collapsible-left">
        <span className="dash-mobile-caret">{coursesOpenMobile ? "▾" : "▸"}</span>
        <span>Courses Overview</span>
      </span>
      <span className="dash-mobile-collapsible-sub">Tap to {coursesOpenMobile ? "collapse" : "expand"}</span>
    </button>

    <div className="dash-mobile-collapsible-body">
      <CourseListPanel
        title="Courses Overview"
        subtitle="Manage all COSMETINK prep courses, assign masters and students, and set class dates."
        courses={gmCourses}
        emptyText="You haven’t seeded any prep courses yet."
        formatDateUTC={formatDateUTC}
      />
    </div>
  </div>
)}

        </section>

        <aside className="dash-column dash-column-side">
          {isGrandMaster && (
            <GMArtistsPanel
              studentSearch={studentSearch}
              setStudentSearch={setStudentSearch}
              masterSearch={masterSearch}
              setMasterSearch={setMasterSearch}
              filteredStudents={filteredStudents}
              filteredMasters={filteredMasters}
              onOpenPerson={openPerson}
              threads={threads}
              pendingApprByStudentId={pendingApprByStudentId}
              photoPackageByStudentId={photoPackageByStudentId}
              socket={socketRef.current}
            />
          )}
        </aside>
      </main>

{selectedPerson && (
  <div className="dash-selected-person-overlay" onClick={handleClosePerson}>
    <div className="dash-selected-person-shell" onClick={(e) => e.stopPropagation()}>
          <SelectedPersonPanel
            selectedPerson={selectedPerson}
            setSelectedPerson={setSelectedPerson}
 refreshSelectedPersonRef={refreshSelectedPersonRef}
              showCourseConfirm={showCourseConfirm}
            setShowCourseConfirm={setShowCourseConfirm}
  courseConfirmText={courseConfirmText}
  setCourseConfirmText={setCourseConfirmText}
  pendingCourseCommit={pendingCourseCommit}
  setPendingCourseCommit={setPendingCourseCommit}
            isCourseSaving={isCourseSaving}
            setIsCourseSaving={setIsCourseSaving}
       isPendingStudent={
  String(selectedPerson?.role || "").toLowerCase() === "student" &&
  !!(selectedPerson?._isPendingStudent || selectedPerson?.isApproved === false || selectedPerson?.status === "pending")
}



            isPendingMaster={!!selectedPerson?._isPendingMaster}
            getEnrollmentsForSelectedPerson={getEnrollmentsForSelectedPerson}
            onClose={handleClosePerson}
            threads={threads}
            currentUser={currentUser}
            gmCourses={gmCourses}
            pendingStudents={gmPendingStudents}
            pendingMasters={gmPendingMasters}
            activeStudents={activeStudents}
            masters={gmMasters}
            approveStudent={approveStudent}
            approveMaster={approveMaster}
            rejectStudent={rejectStudent}
            fetchPendingStudents={fetchPendingStudents}
            fetchActiveStudents={fetchActiveStudents}
            fetchPendingMasters={fetchPendingMasters}
            fetchActiveMasters={fetchActiveMasters}
            fetchMasters={fetchMasters}
            onAssignUserToCourse={onAssignUserToCourse}
            onRemoveUserFromCourse={onRemoveUserFromCourse}
            fetchEnrollmentsForUser={fetchEnrollmentsForUser}
            personEnrollmentsById={personEnrollmentsById}
            setPersonEnrollmentsById={setPersonEnrollmentsById}
            scheduleByPersonId={scheduleByPersonId}
            setScheduleByPersonId={setScheduleByPersonId}
            queuedCoursesByPersonId={queuedCoursesByPersonId}
            setQueuedCoursesByPersonId={setQueuedCoursesByPersonId}
            pendingRemovalsByPersonId={pendingRemovalsByPersonId}
            setPendingRemovalsByPersonId={setPendingRemovalsByPersonId}
            selectedStudentCourseKeys={selectedStudentCourseKeys}
            setSelectedStudentCourseKeys={setSelectedStudentCourseKeys}
            selectedMasterCourseKeys={selectedMasterCourseKeys}
            setSelectedMasterCourseKeys={setSelectedMasterCourseKeys}
            MODEL_TIME_SLOTS_BY_COURSE_KEY={MODEL_TIME_SLOTS_BY_COURSE_KEY}
            requiresModelForCourseKey={requiresModelForCourseKey}
            validateScheduleForKeys={validateScheduleForKeys}
            resolveCourseFromKey={resolveCourseFromKey}
            getScheduleMapForPerson={getScheduleMapForPerson}
            ensureDefaultsForPersonAndKeys={ensureDefaultsForPersonAndKeys}
            getStudentCourseKeysFromUser={getStudentCourseKeysFromUser}
            getTeachingCourseKeysFromUser={getTeachingCourseKeysFromUser}
            getCourseKeyFromEnrollment={getCourseKeyFromEnrollment}
            computeQuizAttemptStats={computeQuizAttemptStats}
            createOrUpdateEnrollment={createOrUpdateEnrollment}
            getGlobalBloodborneFromUser={getGlobalBloodborneFromUser}
            formatDateUTC={formatDateUTC}
            formatDateLocal={formatDateLocal}
            formatDateTimeLocal={formatDateTimeLocal}
            EnrollmentAPI={EnrollmentAPI}
            ApprenticeshipAPI={ApprenticeshipAPI}
            getPersonId ={getPersonId}
            POST_COURSE_MODULES_BY_KEY={POST_COURSE_MODULES_BY_KEY}
            postCourseAttentionByStudentId={postCourseAttentionByStudentId}
            socket={socketRef.current}
          />


        </div></div>
      )}

      <ProfileOverlay
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
        currentUser={currentUser}
        avatarUrl={avatarUrl}
        fullName={fullName}
        isStudent={isStudent}
        certificationEarned={isApprenticeshipCertificationEarned(apprenticeship)}
        instructorName={instructorName}
        getRoleLabel={getRoleLabel}
        getUserDisplayAddress={getUserDisplayAddress}
        triggerAvatarPicker={triggerAvatarPicker}
        onRequestLogout={() => {
          setIsProfileOpen(false);
          setShowLogoutConfirm(true);
        }}
      />

      <LogoutOverlay isOpen={showLogoutConfirm} onClose={() => setShowLogoutConfirm(false)}>
        <LogoutButton />
      </LogoutOverlay>
      </>
      )}
    </div>
  );
};

export default Dashboard;
